/* ============================================================
   EXON UPSELL ISLAND (Track A6) — theme-native popup + cart drawer.

   Vanilla, no framework. 1:1 behavior port of the approved mockup
   (mockups/src/components/Upsell.tsx) re-skinned liquid-glass, and a
   faithful re-implementation of the shipped extension's data layer
   (shopify-apps/shopify-storefront-upsell/src/exon-upsell/*.runtime.js).

   Wiring:
   · window.EXON (exon-store.js) — bus, cart{refresh,add,change,upgrade,
     revert,setAttributes}, overlays{open,closeTop}, mode.
   · Opens: popup on 'exon:cart:added'; drawer on [data-exon-cart-open]
     clicks and after the popup CTA.
   · Config: #exon-upsell-cfg (emitted by snippets/exon-popup.liquid).

   Data strategy (NO fake data):
   · Offers + recs come from the production offers API via the app proxy
     (CONFIG.proxyBase preferred, else CONFIG.offerApiBase). Request +
     response contract mirrors the shipped extension exactly (see OFFERS).
   · Every fetch is wrapped in try/catch with graceful degradation: on
     error/empty the popup renders parent band + trust + CTA only;
     the drawer hides recs + upgrade panels; warranty hides unless a
     variant id is configured. This WILL run before the proxy is live.
   · Telemetry mirrors the extension's /events POSTs behind
     CONFIG.telemetryEnabled; silent no-op on failure.
   · Attribution: every elected /offers response stamps _exon_last_offer_*
     cart attributes (debounced, best-effort) and every accepted offer's
     cart line carries _exon_offer_* properties (finance ingestion reads both).
   · /offers transport self-heals: the app-proxy path is probed first (signed
     identity + browsing context), direct edge on any failure; the winning
     transport is remembered per page. /events + /copy/ensure stay direct.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonUpsellInit) return;
  window.__exonUpsellInit = true;

  /* ---------------- config (single blob, both surfaces) ---------------- */
  function readCfg() {
    var el = document.getElementById('exon-upsell-cfg');
    if (!el || !el.textContent) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }
  var CFG = readCfg();
  var HE = (CFG.locale || 'he').slice(0, 2) !== 'en';
  var LANG = HE ? 'he' : 'en';
  var API_BASE = (
    (CFG.proxyBase && String(CFG.proxyBase).trim()) ||
    (CFG.offerApiBase && String(CFG.offerApiBase).trim()) || ''
  ).replace(/\/+$/, '');
  /* the explicit direct edge base (settings-derived Supabase URL). The
     /offers direct-fallback rung must use THIS — API_BASE prefers proxyBase,
     so a "direct" retry through API_BASE could re-enter the proxy it is
     falling back FROM. Empty when the theme setting is unset (rung skipped). */
  var DIRECT_BASE = ((CFG.offerApiBase && String(CFG.offerApiBase).trim()) || '').replace(/\/+$/, '');
  var OFFERS_ENABLED = !!API_BASE && CFG.enabled !== false;
  var POPUP_ENABLED = CFG.popupEnabled !== false;
  var DRAWER_ENABLED = CFG.cartEnabled !== false;
  var TELEMETRY = OFFERS_ENABLED && CFG.telemetryEnabled !== false;
  var FETCH_TIMEOUT_MS = 4500;
  var POPUP_LIMIT = num(CFG.popupOfferLimit) || 3;
  var CART_LIMIT = num(CFG.cartOfferLimit) || 6;

  /* ---------------- i18n (Hebrew-first; mirrors Upsell.tsx strings) ---------------- */
  var T = {
    he: {
      sec_upgrade: 'שדרוג מהדורה', sec_complete: 'משלימים את החבילה',
      add: 'הוספה', upgrade: 'שדרגו',
      tray: 'נוסף להזמנה', added: 'נוסף', add_just: 'הוסיפו רק',
      no_extra: 'ללא תוספת מחיר', save: 'חוסכים {a}',
      close: 'סגירה',
      cont_none_cart: 'לא תודה, המשך לעגלה', cont_none_checkout: 'לא תודה, המשך לתשלום',
      cont_added_cart_one: 'המשך לעגלה · פריט אחד', cont_added_cart: 'המשך לעגלה · {n} פריטים', cont_added_checkout: 'מעבר לתשלום מאובטח · {t}',
      remove: 'הסרה', undo: 'ביטול השדרוג',
      per_mo: '/ לחודש', once_pay: 'בתשלום אחד', add_once: 'תוספת חד-פעמית',
      unlock: 'מה נפתח בשדרוג', why_fits: 'למה זה מתאים לך', fit_device: 'מתאים לאותו מכשיר', your_mo: 'המחיר שלך לחודש',
      upg_to: 'שדרגו ל-',
      sec_upgrade_ver: 'שדרוג למשחק החדש', ver_label: 'הדור החדש של הסדרה', ver_out: 'יוצא ב-',
      sec_upgrade_sub: 'שדרוג למנוי', sec_upgrade_pack: 'שדרוג החבילה',
      goal_ic_done: 'כל הכבוד!',
      rec_title: 'מומלץ במיוחד עבורכם', rec_ai: 'המלצות חכמות',
      verified: 'רכישה מאומתת',
      axis_term: 'משך מנוי', axis_points: 'כמות נקודות', axis_amount: 'סכום', axis_opt: 'אפשרות',
      gp_your: 'המשחק שלכם', gp_in: ' כלול ב-', gp_in_plan: ' כלול במנוי',
      gp_permo: '/ חודש', gp_term: 'ל-{n} חודשים',
      gp_hint: 'המשחק שלכם + מאות משחקים נוספים, למשל:', gp_more: '+ עוד מאות',
      gp_badge: 'כלול במנוי', bought_badge: 'נקנה יחד',
      r_bought: 'נקנה לעתים קרובות יחד עם {t}', r_popular: 'מהפופולריים בקטגוריה שלו',
      r_companion: 'משתלב מושלם עם ה-{p}', r_bestseller: 'מהנמכרים ביותר ל-{p}',
      r_currency: 'המטבע הרשמי של {t}', r_genre: 'מתאים לחובבי {g}',
      r_genre_anchor: 'עוד משחקי {g} כמו {t}', r_genre_more: 'עוד משחקי {g} מובילים',
      drop_combo: 'קוד ההטבה מהדרופ וההנחה של ההצעה הזו לא יכולים לשמש יחד בקופה.',
      deal_limited: 'מבצע לזמן מוגבל', deal_ends: 'המבצע מסתיים ב־{d}',
      deal_ends_in: 'המבצע מסתיים בעוד', deal_hrs: 'שע׳'
    },
    en: {
      sec_upgrade: 'Edition upgrade', sec_complete: 'Complete the package',
      add: 'Add', upgrade: 'Upgrade',
      tray: 'Added to order', added: 'Added', add_just: 'Add just',
      no_extra: 'No extra cost', save: 'Save {a}',
      close: 'Close',
      cont_none_cart: 'No thanks, continue to cart', cont_none_checkout: 'No thanks, continue to checkout',
      cont_added_cart_one: 'Continue to cart · 1 item', cont_added_cart: 'Continue to cart · {n} items', cont_added_checkout: 'Secure checkout · {t}',
      remove: 'Remove', undo: 'Undo upgrade',
      per_mo: '/ month', once_pay: 'once', add_once: 'One-time add-on',
      unlock: 'What the upgrade unlocks', why_fits: 'Why it fits you', fit_device: 'Fits the same device', your_mo: 'Your monthly price',
      upg_to: 'Upgrade to ',
      sec_upgrade_ver: 'Upgrade to the new game', ver_label: 'The next game in the series', ver_out: 'Releases ',
      sec_upgrade_sub: 'Subscription upgrade', sec_upgrade_pack: 'Pack upgrade',
      goal_ic_done: 'Nice!',
      rec_title: 'Picked for you', rec_ai: 'Smart picks',
      verified: 'Verified purchase',
      axis_term: 'Subscription term', axis_points: 'Points amount', axis_amount: 'Amount', axis_opt: 'Option',
      gp_your: 'Your game', gp_in: ' is included in ', gp_in_plan: ' is included in the plan',
      gp_permo: '/ month', gp_term: 'for {n} months',
      gp_hint: 'Your game + hundreds more, for example:', gp_more: '+ hundreds more',
      gp_badge: 'Included in plan', bought_badge: 'Bought together',
      r_bought: 'Often bought together with {t}', r_popular: 'Popular in its category',
      r_companion: 'Pairs perfectly with your {p}', r_bestseller: 'A top seller for {p}',
      r_currency: 'The official currency of {t}', r_genre: 'A great fit for {g} fans',
      r_genre_anchor: 'More {g} games like {t}', r_genre_more: 'Top {g} games',
      drop_combo: "Your Drop reward code and this offer's discount can't be used together at checkout.",
      deal_limited: 'Limited-time deal', deal_ends: 'Deal ends {d}',
      deal_ends_in: 'Deal ends in', deal_hrs: 'hrs'
    }
  }[LANG];

  /* ---------------- small utils ---------------- */
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function el(sel, ctx) { return (ctx || document).querySelector(sel); }
  function els(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function safeImg(u) {
    u = String(u || '');
    /* /cart.js line items expose featured_image.url as a PROTOCOL-RELATIVE
       url (//cdn.shopify.com/...) — accept it and pin https so the popup
       parent thumbnail loads instead of rendering an empty (black) frame. */
    if (/^\/\//.test(u)) return 'https:' + u;
    return /^https?:\/\//i.test(u) ? u : '';
  }

  /* money: variant/cart JSON prices are integer minor units (agorot).
     Strip HTML from shop money_format (it can embed spans) then fill the
     placeholder — mirrors assets/product.js formatMoney. */
  var MONEY_FMT = String(CFG.moneyFormat || '₪{{amount_no_decimals}}').replace(/<[^>]+>/g, '');
  function commas(n, dec) {
    var parts = n.toFixed(dec).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return dec ? parts.join('.') : parts[0];
  }
  function moneyMinor(minor) {
    var n = (num(minor)) / 100;
    var out = MONEY_FMT
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, commas(n, 0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commas(n, 2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, commas(Math.round(n), 0))
      .replace(/\{\{\s*amount\s*\}\}/g, commas(n, 2));
    if (out === MONEY_FMT) out = '₪' + commas(Math.round(n), 0);
    /* whole shekels never show trailing .00 (mockup: ₪79, not ₪79.00) */
    out = out.replace(/\.00(?!\d)/g, '');
    return out;
  }
  function moneyMajor(major) { return moneyMinor(Math.round(num(major) * 100)); }
  /* per-month prices can carry real agorot (₪45.75/mo) — never round them
     away; whole shekels keep the no-agorot shop format */
  function moneyMinorExact(minor) {
    minor = num(minor);
    if (minor % 100 === 0) return moneyMinor(minor);
    var out = MONEY_FMT.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, commas(minor / 100, 2));
    return out === MONEY_FMT ? '₪' + commas(minor / 100, 2) : out;
  }

  function gidToId(gid) {
    var m = String(gid || '').match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  /* attribution tokens are opaque engine strings — persist one into cart
     data only as a non-empty bounded string, so a malformed payload can't
     push junk (objects, megabyte blobs) into attributes/properties */
  function validToken(t) {
    return (typeof t === 'string' && t.length > 0 && t.length <= 200) ? t : '';
  }
  /* recently-viewed product gids — read-only view of the 'exon:rv' ring
     buffer placements.js owns (newest first); the proxy /offers call sends
     up to 20 as browsing context */
  function recentlyViewed() {
    try {
      var a = JSON.parse(localStorage.getItem('exon:rv') || '[]');
      if (!Array.isArray(a)) return [];
      return a.filter(function (x) {
        return typeof x === 'string' && /^gid:\/\/shopify\/Product\/\d+$/.test(x);
      }).slice(0, 20);
    } catch (e) { return []; }
  }

  /* THE ORDINARY-OFFER DROP CONTEXT — asked of drops.js, the only reader of
     the Drop session store; this island never touches that storage. Absent
     API, a throw, or anything but a bounded opaque string is no context. */
  function dropsOfferContext() {
    try {
      var d = window.EXON && window.EXON.drops;
      var ctx = d && typeof d.offerContext === 'function' ? d.offerContext() : null;
      var ref = ctx && ctx.drops_session_ref;
      return (typeof ref === 'string' && ref.length > 0 && ref.length <= 200)
        ? { drops_session_ref: ref } : null;
    } catch (e) { return null; }
  }

  /* ---------------- inline icons (lucide-equivalent, mockup parity) ---------------- */
  var ICON = {
    x: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    check: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
    sparkles: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.94 14.34A2 2 0 0 0 8.66 13L3.6 11.38a.4.4 0 0 1 0-.76L8.66 9a2 2 0 0 0 1.28-1.34L11.62 2.6a.4.4 0 0 1 .76 0L14.06 7.66A2 2 0 0 0 15.34 9l5.06 1.62a.4.4 0 0 1 0 .76L15.34 13a2 2 0 0 0-1.28 1.34l-1.68 5.06a.4.4 0 0 1-.76 0z"/><path d="M20 3v4M22 5h-4M4 17v2M5 18H3"/></svg>',
    plus: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5v14"/></svg>',
    minus: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg>',
    chevDown: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
    chevUp: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"/></svg>',
    rotate: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>',
    clock: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>',
    gamepad: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="11" y2="11"/><line x1="8" x2="8" y1="9" y2="13"/><line x1="15" x2="15.01" y1="12" y2="12"/><line x1="18" x2="18.01" y1="10" y2="10"/><path d="M17.32 5H6.68a4 4 0 0 0-3.98 3.59c-.01.05-.01.1-.02.15C2.6 9.42 2 14.46 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.41-1.41A2 2 0 0 1 9.83 16h4.34a2 2 0 0 1 1.42.59L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.54-.6-6.58-.68-7.26l-.02-.15A4 4 0 0 0 17.32 5z"/></svg>'
  };
  /* amber-panel verified-badge icon composite (sparkles + check badge) */
  function amodIcon() {
    return '<span class="amod-ic">' + ICON.sparkles +
      '<span class="amod-vb"><svg viewBox="0 0 24 24" width="8" height="8" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span></span>';
  }
  /* inclusion-panel icon composite (gamepad + check badge) */
  function gpIcon() {
    return '<span class="amod-ic">' + ICON.gamepad +
      '<span class="amod-vb"><svg viewBox="0 0 24 24" width="8" height="8" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span></span>';
  }

  /* blur-fill media box (portrait art in a small frame) — mirrors Cover fit.
     eager=true for art inside the just-opened popup modal: lazy imgs paint a
     black square for ~1-2s after the modal appears (they're off-viewport at
     parse time), so surfaces revealed on a user action load eagerly. */
  function coverHtml(imageUrl, title, eager) {
    var u = safeImg(imageUrl);
    if (u) {
      var ld = eager ? 'eager' : 'lazy';
      return '<span class="cover-fit"><img class="cf-blur" src="' + esc(u) + '" alt="" aria-hidden="true" loading="' + ld + '"><img class="cf-img" src="' + esc(u) + '" alt="' + esc(title || '') + '" loading="' + ld + '"></span>';
    }
    /* no image → designed digital-key fallback (matches snippets/key-cover) */
    return '<span class="key-cover"><span class="key-bloom"></span><span class="key-brand mono ltr">EXON · DIGITAL KEY</span><span class="key-lines"></span></span>';
  }

  /* ============================================================
     TELEMETRY — POST {apiBase}/events, batched, best-effort.
     Mirrors the extension: {shop_domain, events:[{event, surface,
     attribution_token, target_id(UUID-only), idempotency_key, metadata}]}.
     Silent no-op on failure and when telemetry is disabled.
     ============================================================ */
  var Events = (function () {
    var queue = [];
    var timer = null;
    function uuid() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    }
    function track(evt) {
      if (!TELEMETRY || !evt || !evt.attribution_token) return;
      if (evt.target_id && !UUID_RE.test(evt.target_id)) delete evt.target_id;
      evt.idempotency_key = evt.idempotency_key || uuid();
      queue.push(evt);
      if (!timer) timer = setTimeout(function () { flush(false); }, 900);
    }
    function flush(useBeacon) {
      if (timer) { clearTimeout(timer); timer = null; }
      if (!TELEMETRY || !queue.length) return;
      var batch = queue.splice(0, queue.length);
      var payload = JSON.stringify({ shop_domain: CFG.canonicalShop, events: batch });
      var u = API_BASE + '/events';
      try {
    /* 🔴 sendBeacon IS NOT USED. The parameter is kept so callers read
       unchanged. Measured on a clean profile: a beacon POST to /events failed
       net::ERR_FAILED with corsError=PreflightInvalidAllowCredentials and
       canceled=false — the preflight OPTIONS returned 200, but the function
       does not send Access-Control-Allow-Credentials, and sendBeacon ALWAYS
       sends credentials cross-origin. A fetch POST to the SAME url seconds
       later returned 200 and persisted, because this fetch omits them.
    
       So the transport that ran on page-hide — exactly when a customer clicks
       checkout and navigates away — was rejected before leaving the browser,
       with no handler and no retry, and the batch was already spliced out of
       the queue. keepalive:true below gives the same page-hide survival,
       defaults to same-origin credentials, and needs NO server change.
       Adding ACAC server-side would be strictly worse: it starts sending
       cookies to a telemetry endpoint, and ACAO:* is illegal with credentials.
    
       ⚠️ Do not restore sendBeacon as an optimisation. It is not a faster path
       here; it is a rejected one. */
        fetch(u, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit', keepalive: true, body: payload
        }).catch(function () { /* best-effort */ });
      } catch (e) { /* ignore */ }
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', function () { flush(true); });
    return { track: track, flush: flush };
  })();

  /* ============================================================
     OFFER ATTRIBUTION — the learning loop's write path.
     (a) postOfferAttributes: every elected /offers response stamps
         _exon_last_offer_* cart attributes via /cart/update.js. Shopify
         MERGES update.js attributes by key (only the keys sent are
         touched), so other cart attributes are never disturbed. Debounced
         so the popup+drawer double-fetch collapses to one write (last
         write wins); fire-and-forget — never breaks the page.
     (b) offerAcceptProps: the _exon_offer_* line-item properties for an
         accepted offer. The underscore prefix keeps them hidden from the
         customer in Shopify — preserve it exactly. Keys with no real
         payload value are omitted (no fake data).
     (c) stampCarriedProps: pre-merges accept properties into a line's
         LIVE /cart.js snapshot so cart.upgrade's property-carry
         (exon-store linePropsByKey) includes them on the target add —
         merge order stays carried → attribution → _original_variant last.
         Client-side only: every refresh rebuilds state from the server,
         so a failed swap leaves no trace.
     ============================================================ */
  var attrDebounce = null;
  function postOfferAttributes(data, surface) {
    var token = data && validToken(data.attributionToken);
    if (!token) return;
    var rid = data.requestId;
    /* canonical-UUID gate: a non-empty invalid primary blocks legacy
       fallbacks server-side — write all four together or nothing */
    if (typeof rid !== 'string' || !UUID_RE.test(rid)) return;
    var attrs = {
      _exon_last_offer_request: rid,
      _exon_last_offer_attribution: token,
      _exon_last_offer_surface: surface,
      _exon_last_offer_seen_at: new Date().toISOString()
    };
    if (attrDebounce) clearTimeout(attrDebounce);
    attrDebounce = setTimeout(function () {
      attrDebounce = null;
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: attrs })
        }).catch(function () { /* best-effort */ });
      } catch (e) { /* never break the page */ }
    }, 600);
  }

  /* ctx: { requestId, attributionToken, surface('popup'|'cart'),
     variantId(accepted variant), position(1-based render position) } */
  function offerAcceptProps(o, ctx) {
    var props = {};
    function put(k, v) {
      if (v == null) return;
      v = String(v);
      if (v) props[k] = v;
    }
    if (!o) return props;
    var rid = ctx.requestId;
    if (typeof rid === 'string' && UUID_RE.test(rid)) put('_exon_offer_request', rid);
    put('_exon_offer_attribution', validToken(ctx.attributionToken));
    /* target is a UUID offer-row id server-side; normalizeOffer falls back to
       the variant GID when target_id is absent — never persist that fallback
       (the accepted variant already rides _exon_offer_variant) */
    if (typeof o.targetId === 'string' && UUID_RE.test(o.targetId)) put('_exon_offer_target', o.targetId);
    if (ctx.variantId) put('_exon_offer_variant', 'gid://shopify/ProductVariant/' + ctx.variantId);
    put('_exon_offer_surface', ctx.surface);
    put('_exon_offer_type', o.offerType);
    put('_exon_relevance_tier', o.tier);
    put('_exon_anchor_variant', o.anchorVariantGid);
    if (ctx.position > 0) put('_exon_position', ctx.position);
    put('_exon_axis', o.axis);
    put('_exon_selection_arm', o.selectionArmId);
    put('_exon_genre_bucket', o.genreBucket);
    put('_exon_anchor_vendor_bucket', o.anchorVendorBucket);
    if (isGamePassInclude(o)) put('_exon_gp_tier', o.gpTier);
    if (o.termMonths > 0) put('_exon_term_months', o.termMonths);
    return props;
  }

  function stampCarriedProps(lineKey, props) {
    if (!props || !Object.keys(props).length) return;
    try {
      var items = (window.EXON.cart.state && window.EXON.cart.state.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i] && String(items[i].key) === String(lineKey)) {
          var merged = Object.assign({}, items[i].properties || {});
          /* a fresh accept tuple replaces ANY earlier one wholesale: drop
             every '_exon_'-prefixed key first (except the placement carrier
             '_exon_plc_*', which belongs to the PDP add, not to an offer) so
             optional fields a previous offer stamped can't survive under
             this offer when it omits them */
          var ks = Object.keys(merged);
          for (var j = 0; j < ks.length; j++) {
            if (ks[j].indexOf('_exon_') === 0 && ks[j].indexOf('_exon_plc_') !== 0) delete merged[ks[j]];
          }
          items[i].properties = Object.assign(merged, props);
          return;
        }
      }
    } catch (e) { /* no snapshot — the accept still works, props just don't carry */ }
  }

  /* ============================================================
     OFFERS CLIENT — POST {apiBase}/offers, cached 60s, aborts stale.
     Request (CORS-simple text/plain to skip preflight; the engine
     parses the body regardless):
       { shop_domain, surface, cart:{lines:[{cart_token, product_gid,
         variant_gid, quantity}]}, requested_offer_limit }
     Response: { ok, request_id, attribution_token, goal,
       groups:[{type,label,offers[]}], offers[] }.
     Never throws — returns null on any failure (graceful degrade).
     ============================================================ */
  var Offers = (function () {
    var cache = {}; // key -> {at, body}
    var inflight = {}; // surface -> AbortController
    var TTL = 60000;
    var MAX_ENTRIES = 128; // insert-order cap so long sessions don't grow unbounded

    function linesFrom(cart) {
      var token = (cart && cart.token) || '';
      return ((cart && cart.items) || []).map(function (it) {
        return {
          cart_token: token,
          product_gid: it.product_id ? 'gid://shopify/Product/' + it.product_id : '',
          variant_gid: (it.variant_id || it.id) ? 'gid://shopify/ProductVariant/' + (it.variant_id || it.id) : '',
          quantity: it.quantity || 1
        };
      });
    }
    function hashLines(lines) {
      return lines.map(function (l) {
        return (l.cart_token || '') + ':' + (l.product_gid || '') + ':' + (l.variant_gid || '') + ':' + (l.quantity || 1);
      }).sort().join('|');
    }

    /* A failed proxy rung gets a short cooldown; each later request chooses
       its own transport so the signed proxy can recover without a reload. */
    var proxyRetryAt = 0;
    var PROXY_RETRY_AFTER_MS = 30000;
    var PROXY_OFFERS_URL = '/apps/exon-offers/upsell-offer-api/offers';

    function fetchOffers(surface, cart, limit, anchorVariantId) {
      if (!OFFERS_ENABLED) return Promise.resolve(null);
      var lines = linesFrom(cart);
      var anchorGid = anchorVariantId ? 'gid://shopify/ProductVariant/' + anchorVariantId : '';
      var k = surface + '#' + limit + '#' + anchorGid + '#' + hashLines(lines);
      /* A DROP-CONTEXT REQUEST IS NEVER CACHED. Its answer depends on a member
         session the cache key does not carry, so it neither reads nor writes
         the 60-second cache; a request without the context is untouched. */
      var dropsCtx = dropsOfferContext();
      var tryProxy = !DIRECT_BASE || Date.now() >= proxyRetryAt;
      var cacheableDirect = !tryProxy;
      var hit = dropsCtx || tryProxy ? null : cache[k];
      if (hit && Date.now() - hit.at < TTL) return Promise.resolve(hit.body);

      if (inflight[surface]) { try { inflight[surface].abort(); } catch (e) {} }
      /* one logical request per surface, but each transport rung gets its OWN
         AbortController + timeout: a timed-out proxy rung must not hand the
         direct rung an already-aborted signal. This wrapper keeps the outer
         stale-abort semantics — aborting a superseded request aborts whichever
         rung is in flight and stops later rungs from starting. */
      var req = {
        stale: false,
        ctrl: null,
        abort: function () {
          req.stale = true;
          if (req.ctrl) { try { req.ctrl.abort(); } catch (e) {} }
        }
      };
      inflight[surface] = req;

      var payload = {
        shop_domain: CFG.canonicalShop,
        surface: surface,
        cart: { lines: lines },
        requested_offer_limit: limit
      };
      /* anchor scopes SCORING to the just-added product server-side; the full
         cart lines still feed exclusion (never offer what's already in cart) */
      if (anchorGid) payload.anchor = { variant_gid: anchorGid };
      /* the opaque Drop session ref, when drops.js hands one out — carried
         IDENTICALLY on both rungs (the proxy object copies it below) */
      if (dropsCtx) payload.drops_session_ref = dropsCtx.drops_session_ref;

      function send(url, bodyObj, creds) {
        if (req.stale) return Promise.reject(new Error('stale'));
        var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        req.ctrl = ctrl;
        var timer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, FETCH_TIMEOUT_MS) : null;
        function settle(x) { if (timer) clearTimeout(timer); return x; }
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8', Accept: 'application/json' },
          credentials: creds,
          signal: ctrl ? ctrl.signal : undefined,
          body: JSON.stringify(bodyObj)
        }).then(function (res) {
          return res.json().then(function (body) { return { res: res, body: body }; });
        }).then(settle, function (err) { settle(); throw err; });
      }
      function viaProxy() {
        /* context only rides the identified proxy call; the direct path is
           anonymous and the server ignores context there — omitted below */
        var p = {
          shop_domain: payload.shop_domain,
          surface: payload.surface,
          cart: payload.cart,
          requested_offer_limit: payload.requested_offer_limit
        };
        if (payload.anchor) p.anchor = payload.anchor;
        if (payload.drops_session_ref) p.drops_session_ref = payload.drops_session_ref;
        p.context = { recently_viewed: recentlyViewed() };
        return send(PROXY_OFFERS_URL, p, 'same-origin').then(function (result) {
          if (!result.res.ok) throw new Error('proxy ' + result.res.status);
          var body = result.body;
          /* a parsed 200 whose body isn't ok:true is NOT a transport win —
             throw so the direct rung gets its turn instead of caching a dud */
          if (!body || body.ok !== true) throw new Error('proxy body not ok');
          proxyRetryAt = 0;
          return body;
        });
      }
      function viaDirect() {
        if (!DIRECT_BASE) return Promise.resolve(null);
        return send(DIRECT_BASE + '/offers', payload, 'omit').then(function (result) {
          if (result.res.ok && result.body && result.body.ok === true) return result.body;
          return null;
        });
      }

      var attempt = tryProxy
        ? viaProxy().catch(function () {
          if (req.stale) return null;
          proxyRetryAt = Date.now() + PROXY_RETRY_AFTER_MS;
          /* Keep the existing ref-free direct fallback cache contract. */
          cacheableDirect = true;
          return viaDirect();
        })
        : viaDirect();

      return attempt.then(function (body) {
        if (body && body.ok === true && !dropsCtx && cacheableDirect) {
          if (!cache[k] && Object.keys(cache).length >= MAX_ENTRIES) {
            var oldest = Object.keys(cache)[0];
            if (oldest) delete cache[oldest];
          }
          cache[k] = { at: Date.now(), body: body };
        }
        return body;
      }).catch(function () {
        return null; // timeout / network / abort — degrade
      }).then(function (r) {
        if (inflight[surface] === req) delete inflight[surface];
        return r;
      });
    }

    /* SELF-HEALING PITCH COPY — when a version/franchise upgrade renders with
       its AI pitch still pending, ask the engine to generate it (fire-and-
       forget; the engine enqueues + generates in the background, so the NEXT
       view of this offer serves the real copy). Once per pair per page. */
    var ensured = {};
    function ensureCopy(offers) {
      var payload = [];
      (offers || []).forEach(function (o) {
        if (!o || !isVersionUpgrade(o)) return;
        if (o.generatedCopy && o.generatedCopy.status === 'ready') return;
        if (!o.anchorVariantGid || !o.variantGid) return;
        var t = o.offerType === 'latest_franchise_release'
          ? 'latest_franchise_release' : 'latest_version_upgrade';
        var k = t + ':' + o.anchorVariantGid + ':' + o.variantGid;
        if (ensured[k]) return;
        ensured[k] = 1;
        payload.push({
          offer_type: t,
          axis: o.axis === 'broaden' ? 'broaden' : 'deepen',
          anchor_variant_gid: o.anchorVariantGid,
          target_variant_gid: o.variantGid
        });
      });
      if (!payload.length) return;
      try {
        fetch(API_BASE + '/copy/ensure', {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          credentials: 'omit',
          keepalive: true,
          body: JSON.stringify({
            shop_domain: CFG.canonicalShop,
            surface: 'popup',
            offers: payload.slice(0, 8)
          })
        }).catch(function () {});
      } catch (e) { /* degrade silently */ }
    }

    return { fetch: fetchOffers, normalize: normalizeResponse, ensureCopy: ensureCopy };
  })();

  /* ---- offer normalization (subset of the extension's normalizeOffer;
     the fields the popup/drawer UI actually renders) ---- */
  function normalizeOffer(o) {
    if (!o) return null;
    return {
      targetId: o.target_id || o.variant_gid || '',
      productGid: o.product_gid || '',
      variantGid: o.variant_gid || '',
      variantId: gidToId(o.variant_gid),
      handle: o.handle || '',
      title: o.title || '',
      variantTitle: o.variant_title || '',
      kind: o.kind || '',
      tier: o.relevance_tier || o.primary_reason || '',
      offerType: (o.offer_type || o.offerType || '').toLowerCase(),
      axis: (o.axis || '').toLowerCase(),
      offerIntent: (o.offer_intent || o.offerIntent || '').toLowerCase(),
      groupKey: (o.group_key || o.groupKey || '').toLowerCase(),
      editionLabel: o.edition_label || '',
      price: num(o.price != null ? o.price : o.price_amount), // MAJOR units
      compareAt: num(o.compare_at || o.compareAt), // MAJOR units, optional
      currency: o.currency || CFG.currency || 'ILS',
      discount: (o.discount && typeof o.discount === 'object') ? o.discount : null,
      image: o.image_url || o.image || null,
      reason: o.reason || o.reason_text || '',
      features: Array.isArray(o.features) ? o.features.slice(0, 3) : [],
      extras: Array.isArray(o.extras) ? o.extras.slice(0, 3) : [],
      termMonths: o.term_months != null ? num(o.term_months) : null,
      perMonthMinor: o.per_month_minor != null ? num(o.per_month_minor) : null,
      fromPerMonthMinor: o.from_per_month_minor != null ? num(o.from_per_month_minor) : null,
      monthlyDropPct: o.monthly_drop_pct != null ? num(o.monthly_drop_pct) : null,
      anchorVariantGid: o.anchor_variant_gid || '',
      anchorPrice: o.anchor_price != null ? num(o.anchor_price) : null, // MAJOR
      cartAction: (o.cart_action || o.cartAction || '').toLowerCase(),
      badge: o.badge || o.badge_label || '',
      reasonCode: String(o.reason_code || o.primary_reason || '').toLowerCase(),
      anchorTitle: o.anchor_title || '',
      /* accept-attribution fields (stamped as _exon_* line properties on
         accepted offers) — absent in older payloads, omitted downstream */
      selectionArmId: o.selection_arm_id != null ? String(o.selection_arm_id) : '',
      genreBucket: o.genre_bucket || '',
      anchorVendorBucket: o.anchor_vendor_bucket || '',
      gpTier: String(o.gp_tier || (o.reason_context && o.reason_context.subscription_tier) || '').toLowerCase(),
      includedGames: normalizeIncludedGames(o.included_games),
      /* version-upgrade pitch: engine-generated AI copy + the context to build
         an honest fallback line while the copy is still generating */
      generatedCopy: normalizeGeneratedCopy(o.generated_copy || o.generatedCopy),
      featureDeltaDisplayable: (o.feature_delta_displayable != null ? o.feature_delta_displayable : o.featureDeltaDisplayable) !== false,
      reasonCtx: normalizeReasonCtx(o.reason_context || o.reasonContext),
      deal: normalizeDeal(o.deal)
    };
  }
  /* the vendor price-drop INDICATOR (Phase 4.1 `deal`). The engine emits it
     only while it scores a qualifying drop and the served price is the
     measured one; anything but `active: true` with a real whole-percent drop
     is no deal. Display-only: it reaches no price, badge or discount path. */
  function normalizeDeal(d) {
    if (!d || typeof d !== 'object' || Array.isArray(d) || d.active !== true) return null;
    var pct = Math.round(num(d.drop_pct != null ? d.drop_pct : d.dropPct));
    if (!(pct > 0 && pct < 100)) return null;
    var endsRaw = d.ends_at != null ? d.ends_at : d.endsAt;
    var endsAtMs = endsRaw ? Date.parse(String(endsRaw)) : NaN;
    return { dropPct: pct, endsAtMs: isFinite(endsAtMs) ? endsAtMs : null };
  }
  function normalizeGeneratedCopy(gc) {
    if (!gc || typeof gc !== 'object') return null;
    return {
      status: String(gc.status || '').toLowerCase(),
      headline: gc.headline || '',
      bullets: Array.isArray(gc.bullets) ? gc.bullets.filter(Boolean).map(String) : []
    };
  }
  function normalizeIncludedGames(list) {
    if (!Array.isArray(list)) return [];
    return list.map(function (g) {
      if (!g || typeof g !== 'object') return null;
      return { title: g.title || '', handle: g.handle || '', image: g.image || g.image_url || '' };
    }).filter(function (g) { return g && g.title; }).slice(0, 6);
  }
  /* engine label maps arrive as {he:[...], en:[...]} — pick the current
     language, fall back to whichever side exists */
  function pickLangList(map) {
    if (!map || typeof map !== 'object') return [];
    var list = Array.isArray(map[LANG]) ? map[LANG] : (map.he || map.en);
    return Array.isArray(list) ? list.filter(Boolean).map(String) : [];
  }
  function normalizeReasonCtx(rc) {
    if (!rc || typeof rc !== 'object') return null;
    return {
      fromTitle: rc.from_title || '',
      toTitle: rc.to_title || '',
      releaseDate: rc.candidate_release_date || '',
      unreleased: rc.candidate_is_unreleased === true,
      anchorTitle: rc.anchor_title || '',
      supportCount: rc.support_count != null ? num(rc.support_count) : null,
      orders90d: rc.orders_90d != null ? num(rc.orders_90d) : null,
      platformLabel: rc.platform_label || '',
      sharedGenreLabels: pickLangList(rc.shared_genre_labels),
      sharedDeviceLabels: pickLangList(rc.shared_device_labels),
      subscriptionTier: String(rc.subscription_tier || '').toLowerCase(),
      verticalKey: rc.vertical_key || ''
    };
  }
  function normalizeResponse(body) {
    if (!body || !body.ok) return null;
    var groups = Array.isArray(body.groups) ? body.groups : [];
    function normList(arr) {
      return (Array.isArray(arr) ? arr : []).map(normalizeOffer).filter(Boolean);
    }
    return {
      requestId: body.request_id || null,
      attributionToken: body.attribution_token || null,
      goal: body.goal || null,
      groups: groups.map(function (g) {
        return { type: (g.type || 'cross_sell').toLowerCase(), label: g.label || '', offers: normList(g.offers) };
      }).filter(function (g) { return g.offers.length; }),
      offers: normList(body.offers),
      /* THE EDGE-OWNED DROP CONTEXT FIELDS, carried under their wire names.
         Absent (an older server) reads as 'none' / unknown combination. */
      campaign_context_state: CONTEXT_STATE_RE.test(body.campaign_context_state || '')
        ? body.campaign_context_state : 'none',
      campaign_context_revision: typeof body.campaign_context_revision === 'string'
        ? body.campaign_context_revision : null,
      combination: normalizeCombination(body.combination)
    };
  }
  var CONTEXT_STATE_RE = /^(none|applied|invalid)$/;
  var COMBINATION_SOURCE_RE = /^(none|choice_grant|referral_config)$/;
  /* anything that is not a known answer with all three booleans is UNKNOWN —
     and an unknown combination never produces a notice */
  function normalizeCombination(c) {
    if (!c || typeof c !== 'object' || Array.isArray(c) || c.known !== true ||
        typeof c.order_discounts !== 'boolean' || typeof c.product_discounts !== 'boolean' ||
        typeof c.shipping_discounts !== 'boolean') {
      return { known: false, source: 'none', order_discounts: null, product_discounts: null, shipping_discounts: null };
    }
    return {
      known: true,
      source: COMBINATION_SOURCE_RE.test(c.source || '') ? c.source : 'none',
      order_discounts: c.order_discounts,
      product_discounts: c.product_discounts,
      shipping_discounts: c.shipping_discounts
    };
  }

  /* THE COMPATIBILITY NOTICE — compatibility only. Shown for a DISCOUNTED
     offer when the response that produced it says the member's Drop context
     was applied and the Drop reward's discount is known NOT to combine with
     product discounts. It never says the Drop benefit applies or is on the
     order; it applies, removes and reads no discount code, and it does not
     block or change the add. One line, rendered with the offer, so the
     customer sees it before choosing to add. */
  function dropComboNotice(data, o) {
    if (!data || !o || data.campaign_context_state !== 'applied') return false;
    var c = data.combination;
    if (!c || c.known !== true || c.product_discounts !== false) return false;
    return !!(o.discount && num(o.discount.pct) > 0);
  }
  function dropComboNoteHtml(data, o) {
    return dropComboNotice(data, o)
      ? '<span class="up-drop-note" data-up-drop-note role="note">' + esc(T.drop_combo) + '</span>'
      : '';
  }

  /* THE DEAL LINE (.up-deal) — the vendor's own price drop, one line under the
     reason line (approved mockup v2, 2026-09-23). Three states, one place:
     no end date → "limited-time deal  -N%"; an end date → "ends D/M"; an end
     within 48 h → a live hh:mm countdown (DealClock). Never the badge slot,
     never a strikethrough, never read by price math or `discount`. A deal
     whose end has passed renders nothing. data-up-deal names the state. */
  var DEAL_SOON_MS = 48 * 3600000;
  var DEAL_TICK_MS = 15000;
  function dealHhMm(leftMs) {
    var h = Math.floor(leftMs / 3600000), m = Math.floor((leftMs % 3600000) / 60000);
    return (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
  }
  function dealDate(ms) {
    var d = new Date(ms);
    return d.getDate() + '/' + (d.getMonth() + 1);
  }
  function dealLineHtml(o, now) {
    var d = o && o.deal;
    if (!d) return '';
    var head = '<span class="up-deal';
    if (d.endsAtMs == null) {
      return head + '" data-up-deal="limited">' + ICON.clock + '<i>' + esc(T.deal_limited) + '</i>' +
        '<b class="ltr">-' + d.dropPct + '%</b></span>';
    }
    var left = d.endsAtMs - (now == null ? Date.now() : now);
    if (left <= 0) return '';
    if (left <= DEAL_SOON_MS) {
      return head + ' soon" data-up-deal="countdown">' + ICON.clock + '<i>' + esc(T.deal_ends_in) + '</i>' +
        '<b class="ltr" data-up-deal-end="' + d.endsAtMs + '">' + dealHhMm(left) + '</b><em>' + esc(T.deal_hrs) + '</em></span>';
    }
    return head + '" data-up-deal="ends">' + ICON.clock + '<i>' + esc(T.deal_ends.replace('{d}', dealDate(d.endsAtMs))) + '</i></span>';
  }
  /* the deal is about the SERVED variant — a customer who picks another
     variant in the selector no longer sees a claim about a price it isn't */
  function syncDealLine(card, offer, selId) {
    var line = card && el('[data-up-deal]', card);
    if (line) line.hidden = !(offer && offer.variantId && String(selId) === String(offer.variantId));
  }
  /* one ticker for every live countdown on the open surfaces. A surface is
     watched after it paints and released when it closes; the interval runs
     only while a countdown node is live and stops itself otherwise. An ended
     countdown removes its line (the deal is over). */
  var DealClock = (function () {
    var roots = [], timer = null;
    function halt() { if (timer) { clearInterval(timer); timer = null; } }
    function paint() {
      var now = Date.now(), live = 0;
      /* a surface hidden without its close hook is released here */
      roots = roots.filter(function (r) { return !r.hidden; });
      roots.forEach(function (r) {
        els('[data-up-deal-end]', r).forEach(function (b) {
          var left = num(b.getAttribute('data-up-deal-end')) - now;
          if (left > 0) { b.textContent = dealHhMm(left); live++; return; }
          var line = b.closest('[data-up-deal]');
          if (line && line.parentNode) line.parentNode.removeChild(line);
        });
      });
      if (!live) halt();
      return live;
    }
    function watch(root) {
      if (!root) return;
      if (roots.indexOf(root) < 0) roots.push(root);
      if (paint() && !timer) timer = setInterval(paint, DEAL_TICK_MS);
    }
    function release(root) {
      roots = roots.filter(function (r) { return r !== root; });
      if (!roots.length) halt(); else paint();
    }
    return { watch: watch, release: release };
  })();

  /* ---- offer taxonomy: upgrade (swap) vs complementary (add) ----
     AUTHORITATIVE signal = cart_action: "swap" is an upgrade (replaces the
     anchor line), "add" is a cross-sell. When cart_action is absent (legacy
     / cached payloads) fall back to offer_type/tier/group_key/intent. This
     mirrors the extension's isSwapOffer contract. */
  var UPGRADE_TYPES = /^(edition_upgrade|latest_version_upgrade|subscription_tier_upgrade|subscription_term_upgrade|term_upgrade|subscription_includes_game|game_pass_upgrade)$/;
  var UPGRADE_GROUPS = /^(edition|latest_version|subscription_tier|subscription_term|game_pass)$/;
  function isUpgrade(o) {
    if (!o) return false;
    if (o.cartAction === 'swap') return true;
    if (o.cartAction === 'add') return false;
    return UPGRADE_TYPES.test(o.offerType) || UPGRADE_TYPES.test(o.tier) ||
      UPGRADE_GROUPS.test(o.groupKey) || o.offerIntent === 'upgrade';
  }
  /* subscription-inclusion (Game Pass family): the anchor game is INSIDE the
     offered plan. An upgrade (swap) by classification, but pitched as
     inclusion — never keyed off cart_action alone (cached payloads may omit it). */
  var GAMEPASS_TYPES = /^(subscription_includes_game|game_pass_upgrade)$/;
  function isGamePassInclude(o) {
    if (!o) return false;
    return GAMEPASS_TYPES.test(o.offerType) || GAMEPASS_TYPES.test(o.tier) || o.groupKey === 'game_pass';
  }
  /* GAME upgrade (GTA 5 → GTA 6), not an edition upgrade of the same game.
     These carry a different pitch: the engine's generated copy (or an honest
     new-generation fallback), never the edition feature-delta template. */
  var VERSION_UPGRADE_TYPES = /^(latest_version_upgrade|latest_franchise_release)$/;
  function isVersionUpgrade(o) {
    if (!o) return false;
    return VERSION_UPGRADE_TYPES.test(o.offerType) || VERSION_UPGRADE_TYPES.test(o.tier) ||
      o.groupKey === 'latest_version' || o.groupKey.indexOf('latest_franchise') === 0;
  }
  /* section header for the popup's upgrade group, by what is actually being
     upgraded: the game generation, a subscription, a currency/points pack, or
     (default) the edition */
  function upgradeSectionLabel(o) {
    if (isGamePassInclude(o)) return T.sec_upgrade_sub;
    if (isVersionUpgrade(o)) return T.sec_upgrade_ver;
    var t = (o && o.offerType) || '';
    if (t === 'subscription_tier_upgrade' || t === 'subscription_term_upgrade' ||
        t === 'term_upgrade' || t === 'subscription_includes_game') return T.sec_upgrade_sub;
    if (t === 'currency_pack_upsell') return T.sec_upgrade_pack;
    return T.sec_upgrade;
  }
  /* "Grand Theft Auto VI | GTA 6 (Ultimate Edition) - Xbox" → "GTA 6 (Ultimate Edition)" */
  function shortTitle(t) {
    t = String(t || '').split(' - ')[0];
    var i = t.lastIndexOf('|');
    if (i >= 0) t = t.slice(i + 1);
    return t.trim() || String(t || '').trim();
  }
  function fmtDateDMY(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? m[3] + '.' + m[2] + '.' + m[1] : '';
  }

  /* the anchor as a reason line names it (EXO-4667 change 2): the short
     title without its edition, platform or parenthetical, e.g. "ACE COMBAT™
     7: SKIES UNKNOWN (Standard Edition) - למחשב" → "ACE COMBAT™ 7: SKIES
     UNKNOWN". A cut that leaves nothing keeps the short title. */
  var EDITION_TAIL_RE = /\s*[:\-–—]?\s+(?:Game\s+of\s+the\s+Year|(?:(?:Digital|Super|Game\s+of\s+the\s+Year)\s+)?[^\s()]+)\s+Edition\s*$/i;
  var PLATFORM_TAIL_RE = /\s+(?:PC|PS4|PS5|Steam|Xbox(?:\s+One|\s+Series\s+X\|S|\s+Series\s+[XS])?|Nintendo\s+Switch(?:\s+2)?|Switch(?:\s+2)?|למחשב)\s*$/i;
  function reasonAnchor(t) {
    var short = shortTitle(t);
    /* parentheticals go first: an edition can carry ' - ' inside its own
       parentheses ("(Maverick - Edition)"), which shortTitle reads as a
       platform suffix */
    var cut = shortTitle(String(t || '').replace(/\s*\([^)]*\)/g, '').replace(/\s*\[[^\]]*\]/g, ''));
    for (var i = 0; i < 2; i++) cut = cut.replace(PLATFORM_TAIL_RE, '').replace(EDITION_TAIL_RE, '').trim();
    cut = cut.replace(/[\s\-–—:|,]+$/, '').replace(/\s{2,}/g, ' ').trim();
    return cut || short;
  }
  /* honest one-line evidence for an offer — deterministic templates keyed by
     reason_code. No evidence → no line; price/discount never appear here.
     Inclusion offers return '' (their amber panel already tells the story).
     `sub`, when given, stands in for the anchor in the sentence (the markup
     builder below puts its <bdi> there). */
  var REASON_MARK = '\u0001';
  function reasonLineWith(o, sub) {
    if (!o) return '';
    var rc = o.reasonCtx || {};
    var code = o.reasonCode || '';
    if (isGamePassInclude(o) || code === 'subscription_includes_selected_game') return '';
    var anchor = reasonAnchor(o.anchorTitle || rc.anchorTitle || '');
    var put = function () { return sub == null ? anchor : sub; };
    if (code === 'bought_together') return anchor ? T.r_bought.replace('{t}', put) : '';
    if (code === 'same_game_currency') return anchor ? T.r_currency.replace('{t}', put) : '';
    /* the smartest honest line references the ANCHOR through their shared
       genres — 'עוד משחקי ספורט ומירוצים כמו FC 26' (owner directive: every
       arm has a reason; say it like a merchant, not a label). Game offers
       with real shared genres take this over the platform lines. */
    if (o.kind === 'game') {
      var gs = realLabels(rc.sharedGenreLabels, 2);
      if (gs.length) {
        var g = gs.length > 1 ? (HE ? gs[0] + ' ו' + gs[1] : gs[0] + ' & ' + gs[1]) : gs[0];
        return anchor ? T.r_genre_anchor.replace('{g}', function () { return g; }).replace('{t}', put)
                      : T.r_genre_more.replace('{g}', function () { return g; });
      }
    }
    if (code === 'same_category_popular') return T.r_popular;
    if (code === 'platform_companion') return rc.platformLabel ? T.r_companion.replace('{p}', rc.platformLabel) : '';
    if (code === 'platform_bestseller') return rc.platformLabel ? T.r_bestseller.replace('{p}', rc.platformLabel) : '';
    var genre = firstRealLabel(rc.sharedGenreLabels);
    return genre ? T.r_genre.replace('{g}', genre) : '';
  }
  /* the reason line as escaped markup, the anchor in its own <bdi> (Latin
     inside Hebrew stays whole and in order); in a one-line slot the CSS makes
     the anchor the part that shrinks, so the ellipsis clips its END
     (upsell.css .rs-a) */
  function reasonLineHtml(o) {
    var line = reasonLineWith(o, REASON_MARK);
    if (!line) return '';
    var parts = line.split(REASON_MARK);
    if (parts.length < 2) return esc(line);
    var rc = (o && o.reasonCtx) || {};
    var anchor = reasonAnchor(o.anchorTitle || rc.anchorTitle || '');
    var tail = parts.slice(1).join('');
    return (parts[0] ? '<span class="rs-p">' + esc(parts[0]) + '</span>' : '') +
      '<bdi class="rs-a" dir="' + (/[\u0590-\u05FF]/.test(anchor) ? 'rtl' : 'ltr') + '">' + esc(anchor) + '</bdi>' +
      (tail ? '<span class="rs-p">' + esc(tail) + '</span>' : '');
  }
  function realLabels(list, max) {
    var out = [];
    if (Array.isArray(list)) {
      for (var i = 0; i < list.length && out.length < max; i++) {
        var v = String(list[i] || '').trim();
        if (v && v.toLowerCase() !== 'other') out.push(v);
      }
    }
    return out;
  }
  function firstRealLabel(list) {
    if (!Array.isArray(list)) return '';
    for (var i = 0; i < list.length; i++) {
      var v = String(list[i] || '').trim();
      if (v && v.toLowerCase() !== 'other') return v;
    }
    return '';
  }

  /* the live cart line an upgrade offer swaps FROM, matched by its anchor
     variant — the resolution both the delta display and a real swap share */
  function anchorLine(o) {
    if (!o || !o.anchorVariantGid) return null;
    var id = gidToId(o.anchorVariantGid);
    if (!id) return null;
    var items = (window.EXON && window.EXON.cart && window.EXON.cart.state && window.EXON.cart.state.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (String(it.variant_id || it.id) === String(id)) return it;
    }
    return null;
  }
  /* anchor line price (minor units) from live cart state — the upgrade-delta
     fallback when an offer payload carries no anchor_price */
  function anchorLineMinor(o) {
    var it = anchorLine(o);
    if (!it) return null;
    return num(it.final_price != null ? it.final_price : it.price);
  }
  /* signed upgrade delta — never clamped: an equal-priced target is free and a
     cheaper one is a saving, and the label must agree with what the swap will
     actually charge. deltaCls pairs the phrase variants with their styling
     (plaintext bidi so Hebrew and English both read correctly). */
  function deltaText(deltaMinor) {
    if (deltaMinor === 0) return T.no_extra;
    if (deltaMinor < 0) return T.save.replace('{a}', moneyMinor(-deltaMinor));
    return '+' + moneyMinor(deltaMinor);
  }
  function deltaCls(deltaMinor) {
    return deltaMinor === 0 ? 'up-delta-zero' : (deltaMinor < 0 ? 'up-delta-save' : 'ltr');
  }

  /* split a normalized response into { upgrades[], complementary[] }.
     Prefer server groups (type carries intent); else fall back to the
     per-offer taxonomy. Dedup by productGid, cap complementary.
     R1 (owner-approved mockup v2, 2026-09-23): up to TWO upgrades — the first
     non-Game-Pass upgrade (edition, version, tier…) then the first Game Pass
     inclusion (the engine's companion) — and they sit OUTSIDE the
     complementary budget: `limit` caps the complementary list only. */
  function splitOffers(data, limit) {
    var upgrades = [], comps = [], seen = {};
    function push(list, o) {
      var id = o.productGid || o.variantGid || o.handle;
      if (!id || seen[id]) return;
      seen[id] = 1; list.push(o);
    }
    if (data.groups && data.groups.length) {
      data.groups.forEach(function (g) {
        var up = UPGRADE_TYPES.test(g.type) || UPGRADE_GROUPS.test(g.type);
        g.offers.forEach(function (o) { push(up || isUpgrade(o) ? upgrades : comps, o); });
      });
    }
    (data.offers || []).forEach(function (o) { push(isUpgrade(o) ? upgrades : comps, o); });
    return { upgrades: pickUpgrades(upgrades), complementary: comps.slice(0, Math.max(0, limit || 3)) };
  }
  function pickUpgrades(list) {
    var primary = null, gamePass = null;
    list.forEach(function (o) {
      if (isGamePassInclude(o)) { if (!gamePass) gamePass = o; }
      else if (!primary) primary = o;
    });
    return [primary, gamePass].filter(Boolean);
  }

  /* ============================================================
     PRODUCTS CLIENT — lazy-fetch live Shopify product JSON so the
     variant selectors show current options / prices / availability
     (the source of truth where the customer pays). Cached 5 min,
     never throws (selector just won't render on failure).
     ============================================================ */
  var Products = (function () {
    var root = String(CFG.routesRoot || '/').replace(/\/+$/, '') + '/';
    var cache = {}, inflight = {};
    var TTL = 300000;
    var MAX_ENTRIES = 128; // insert-order cap so long sessions don't grow unbounded
    function store(handle, product) {
      if (!cache[handle] && Object.keys(cache).length >= MAX_ENTRIES) {
        var oldest = Object.keys(cache)[0];
        if (oldest) delete cache[oldest];
      }
      cache[handle] = { at: Date.now(), product: product };
    }
    function get(handle) {
      handle = String(handle || '').trim();
      if (!handle) return Promise.resolve(null);
      var hit = cache[handle];
      if (hit && Date.now() - hit.at < TTL) return Promise.resolve(hit.product);
      if (inflight[handle]) return inflight[handle];
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, FETCH_TIMEOUT_MS) : null;
      var p = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }, credentials: 'same-origin',
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) { store(handle, prod); return prod; })
        .catch(function () { store(handle, null); return null; })
        .then(function (prod) { if (timer) clearTimeout(timer); delete inflight[handle]; return prod; });
      inflight[handle] = p;
      return p;
    }
    return { get: get };
  })();

  /* LIVE PRICES (EXO-4724). The offers API serves a CACHED price that can
     trail Shopify, and a card must print what the cart will charge. So every
     offer's price is resolved here, once, from the live product JSON, before
     either surface renders: `price` becomes the live price (MAJOR units) of
     the variant the offer targets. An offer with NO live price is REMOVED —
     the cached number is never printed as a price. That is: no handle, the
     product fetch failed, the target variant is not in the product, it cannot
     be bought (available === false), or its price is not a finite number.
     The cached anchor price is dropped as well, so an upgrade's delta is only
     ever computed against the anchor's LIVE cart line. */
  function withLivePrices(data) {
    function live(o) {
      if (!o.handle) return Promise.resolve(null);
      return Products.get(o.handle).then(function (prod) {
        var v = prod ? (prod.variants || []).filter(function (x) { return String(x.id) === String(o.variantId); })[0] : null;
        if (!v || v.available === false) {
          /* the target cannot be bought, but a product with 2+ buyable
             variants still gets its selector (hydrateSelectors /
             hydrateRecSelectors pick an available one and print ITS live
             price). Keep the offer for that; until the selector paints, the
             number is a live one — the first buyable variant's */
          var buyable = prod ? (prod.variants || []).filter(function (x) {
            return x.available !== false && typeof x.price === 'number' && isFinite(x.price);
          }) : [];
          if (buyable.length < 2) return null;
          v = buyable[0];
        }
        if (typeof v.price !== 'number' || !isFinite(v.price)) return null;
        o.price = v.price / 100;
        o.anchorPrice = null;
        return o;
      });
    }
    function liveList(list) {
      return Promise.all(list.map(live)).then(function (kept) { return kept.filter(Boolean); });
    }
    return Promise.all([liveList(data.offers)].concat(data.groups.map(function (g) { return liveList(g.offers); })))
      .then(function (lists) {
        data.offers = lists[0];
        data.groups.forEach(function (g, i) { g.offers = lists[i + 1]; });
        data.groups = data.groups.filter(function (g) { return g.offers.length; });
        return data;
      });
  }

  function axisLabel(variants) {
    var v = variants && variants[0];
    if (!v) return T.axis_opt;
    /* infer axis from the option name (best-effort; matches mockup axisLabel) */
    var name = String((v.options && v.options[0]) || v.title || '').toLowerCase();
    if (/month|חודש|term|מנוי/.test(name)) return T.axis_term;
    if (/point|נקוד|pts/.test(name)) return T.axis_points;
    if (/\$|usd|amount|סכום|טעינ/.test(name)) return T.axis_amount;
    return T.axis_opt;
  }

  /* ============================================================
     AMBER ENRICHMENT PANEL (.amod) — the three recipes from the
     mockup: per-month (subscription), feature list, or a reason value.
     ============================================================ */
  function amodPerMonth(label, nowMinor, wasMinor, savePct, note) {
    var s = '<div class="amod">' + amodIcon() + '<div class="amod-copy">';
    s += '<span class="amod-label">' + esc(label) + '</span>';
    s += '<div class="amod-pm"><b class="ltr">' + esc(moneyMinor(nowMinor)) + '</b><span class="pm-unit">' + esc(T.per_mo) + '</span>';
    if (wasMinor) s += '<s class="ltr">' + esc(moneyMinor(wasMinor)) + '</s>';
    if (savePct) s += '<em class="ltr">↓ ' + esc(savePct) + '%</em>';
    if (note) s += '<span class="pm-note">' + esc(note) + '</span>';
    s += '</div></div></div>';
    return s;
  }
  function amodFeats(label, feats) {
    var s = '<div class="amod">' + amodIcon() + '<div class="amod-copy">';
    s += '<span class="amod-label">' + esc(label) + '</span><ul class="amod-feats">';
    feats.forEach(function (f) { s += '<li><i>+</i> ' + esc(f) + '</li>'; });
    s += '</ul></div></div>';
    return s;
  }
  function amodValue(label, value, feats) {
    var s = '<div class="amod">' + amodIcon() + '<div class="amod-copy">';
    s += '<span class="amod-label">' + esc(label) + '</span>';
    if (value) s += '<b class="amod-value">' + esc(value) + '</b>';
    if (feats && feats.length) {
      s += '<ul class="amod-feats">';
      feats.forEach(function (f) { s += '<li><i>+</i> ' + esc(f) + '</li>'; });
      s += '</ul>';
    }
    s += '</div></div>';
    return s;
  }
  /* label + upgrade-to value + a per-month row (drawer term-upgrade panel) */
  function amodValuePerMonth(label, value, nowMinor, wasMinor, savePct, note) {
    var s = '<div class="amod">' + amodIcon() + '<div class="amod-copy">';
    s += '<span class="amod-label">' + esc(label) + '</span>';
    if (value) s += '<b class="amod-value">' + esc(value) + '</b>';
    s += '<div class="amod-pm"><b class="ltr">' + esc(moneyMinor(nowMinor)) + '</b><span class="pm-unit">' + esc(T.per_mo) + '</span>';
    if (wasMinor) s += '<s class="ltr">' + esc(moneyMinor(wasMinor)) + '</s>';
    if (savePct) s += '<em class="ltr">↓ ' + esc(savePct) + '%</em>';
    if (note) s += '<span class="pm-note">' + esc(note) + '</span>';
    s += '</div></div></div>';
    return s;
  }

  /* version-upgrade amber panel: the engine's AI pitch (headline + bullets)
     when generated; honest new-generation fallback while it's pending. The
     edition feature-delta is the wrong story here (the engine flags it
     feature_delta_displayable=false) — never render it for these. */
  function versionAmod(o) {
    var gc = o.generatedCopy;
    if (gc && gc.status === 'ready' && gc.bullets.length) {
      return amodFeats(gc.headline || T.ver_label, gc.bullets.slice(0, 3));
    }
    var feats = [];
    if (o.reasonCtx && o.reasonCtx.unreleased && o.reasonCtx.releaseDate) {
      var d = fmtDateDMY(o.reasonCtx.releaseDate);
      if (d) feats.push(T.ver_out + d);
    }
    return amodValue(T.ver_label, T.upg_to + shortTitle(o.title), feats);
  }

  /* subscription-inclusion amber panel (locked design 2026-06-03):
     "{anchor} כלול ב-{plan}" · ₪/mo for N months · up to 3 REAL included-game
     chips (anchor filtered out) + a "+ hundreds more" chip. No included
     games → no breadth claim; no per-month data → no price line. */
  var GP_TIER_LABELS = {
    ultimate: 'Game Pass Ultimate', premium: 'Game Pass Premium',
    essential: 'Game Pass Essential', pc_game_pass: 'PC Game Pass', ea_play: 'EA Play'
  };
  /* chip display normalization, reused for the anchor comparison: engine and
     Shopify title variants (edition parentheticals, platform suffixes) must
     not defeat the anchor de-dup */
  function chipNorm(t) {
    return shortTitle(String(t || '')).replace(/\s*\([^)]*\)/g, '').replace(/\s{2,}/g, ' ').trim();
  }
  function includedChips(o) {
    var anchor = chipNorm(o.anchorTitle || (o.reasonCtx && o.reasonCtx.anchorTitle) || '').toLowerCase();
    var out = [];
    (o.includedGames || []).forEach(function (g) {
      if (out.length >= 3) return;
      var t = String(g.title || '').trim();
      if (!t) return;
      var name = chipNorm(t) || shortTitle(t);
      if (anchor && name.toLowerCase() === anchor) return; // the anchor itself
      out.push({ name: name, image: g.image });
    });
    return out;
  }
  function gamepassAmod(o) {
    var rc = o.reasonCtx || {};
    var plan = o.editionLabel || GP_TIER_LABELS[rc.subscriptionTier] || '';
    var anchor = shortTitle(o.anchorTitle || rc.anchorTitle || '');
    var label = (anchor || T.gp_your) + (plan ? T.gp_in + plan : T.gp_in_plan);
    var s = '<div class="amod amod-gp">' + gpIcon() + '<div class="amod-copy">';
    s += '<span class="amod-label">' + esc(label) + '</span>';
    if (o.perMonthMinor) {
      var unit = T.gp_permo + (o.termMonths ? ' · ' + T.gp_term.replace('{n}', o.termMonths) : '');
      s += '<div class="amod-pm"><b class="ltr">' + esc(moneyMinorExact(o.perMonthMinor)) + '</b><span class="pm-unit">' + esc(unit) + '</span></div>';
    }
    var chips = includedChips(o);
    if (chips.length) {
      s += '<span class="amod-gp-hint">' + esc(T.gp_hint) + '</span><div class="amod-gp-chips">';
      chips.forEach(function (g) {
        var u = safeImg(g.image);
        s += '<span class="gp-chip">' + (u ? '<img src="' + esc(u) + '" alt="" loading="lazy">' : '') + '<i>' + esc(g.name) + '</i></span>';
      });
      s += '<span class="gp-chip gp-chip-more">' + esc(T.gp_more) + '</span></div>';
    }
    s += '</div></div>';
    return s;
  }

  /* the amber panel for one offer, given its live variant (if any) */
  function offerAmod(o, variant) {
    var up = isUpgrade(o);
    if (isGamePassInclude(o)) return gamepassAmod(o);
    if (isVersionUpgrade(o)) return versionAmod(o);
    if (variant && variant.options) {
      /* subscription term → per-month recipe from the live variant price */
      var months = inferMonths(variant);
      if (months && months > 0) {
        var nowMinor = Math.round(variant.price / months);
        var wasMinor = variant.compare_at_price ? Math.round(variant.compare_at_price / months) : 0;
        var save = wasMinor ? Math.round((1 - variant.price / variant.compare_at_price) * 100) : 0;
        return amodPerMonth(T.your_mo, nowMinor, wasMinor, save, (variant.public_title || variant.title || '') + ' · ' + moneyMinor(variant.price) + ' ' + T.once_pay);
      }
    }
    if (o.perMonthMinor) {
      return amodPerMonth(T.your_mo, o.perMonthMinor, o.fromPerMonthMinor || 0, o.monthlyDropPct || 0, '');
    }
    var feats = o.featureDeltaDisplayable === false
      ? []
      : ((o.extras && o.extras.length ? o.extras : o.features) || []);
    if (!up && feats.length) return crossFitAmod(o, feats);
    if (feats.length) return amodFeats(up ? T.unlock : T.why_fits, feats.slice(0, 3));
    return '';
  }
  /* A CROSS-SELL's "why it fits" box (EXO-4667 change 3). The engine's
     cross-sell lanes fill features[] with fixed generic lines (EXON Database
     migrations, e.g. c2_category_lanes / c5_platform_popular_fallback):
     - the same-device line ('משחקים דומים לאותו מכשיר', 'משחק דומה שמתאים
       לאותו מכשיר') becomes one pill, '✦ מתאים לאותו מכשיר';
     - a line that only repeats the reason line shown above it (co-purchase,
       category-popular, platform companion / bestseller) is dropped.
     Any other feature is real content: the box stays, as before. Upgrades,
     inclusion panels and per-month boxes never come here. */
  var SAME_DEVICE_RE = /לאותו מכשיר|same device/i;
  var REASON_ECHO = [
    ['r_bought', /נרכש הרבה יחד|bought together/i],
    ['r_popular', /פופולריים באותה קטגוריה|popular in (?:its|the same) category/i],
    ['r_companion', /משלים לפלטפורמה|platform companion/i],
    ['r_bestseller', /פופולרי לאותה פלטפורמה|popular (?:for|on) the same platform/i]
  ];
  function reasonEcho(o) {
    var line = reasonLineWith(o, null);
    if (!line) return null;
    for (var i = 0; i < REASON_ECHO.length; i++) {
      var lead = String(T[REASON_ECHO[i][0]] || '').split('{')[0];
      if (lead && line.indexOf(lead) === 0) return REASON_ECHO[i][1];
    }
    return null;
  }
  function crossFitAmod(o, feats) {
    var echo = reasonEcho(o), device = false, real = false;
    feats.forEach(function (f) {
      var t = String(f || '');
      if (SAME_DEVICE_RE.test(t)) device = true;
      else if (!(echo && echo.test(t))) real = true;
    });
    if (real) return amodFeats(T.why_fits, feats.slice(0, 3));
    if (device) return '<span class="up-fit"><i aria-hidden="true">✦</i>' + esc(T.fit_device) + '</span>';
    return '';
  }
  function inferMonths(variant) {
    var t = String((variant && (variant.public_title || variant.title)) || '');
    var m = t.match(/(\d+)\s*(?:month|mo|חודש)/i);
    if (m) return num(m[1]);
    if (/\b12\b/.test(t) || /year|שנה|שנתי/i.test(t)) return 12;
    return 0;
  }

  /* labeled variant selector (.gsel) — options from live product JSON */
  function selectorHtml(variants, selId) {
    if (!variants || variants.length < 2) return '';
    var s = '<div class="up-sels"><div class="up-selwrap"><span class="up-sellbl">' + esc(axisLabel(variants)) + '</span>';
    s += '<label class="gsel"><select data-up-sel>';
    variants.forEach(function (v) {
      s += '<option value="' + esc(v.id) + '"' + (String(v.id) === String(selId) ? ' selected' : '') + '>' +
        esc(v.public_title || v.title || v.name) + ' · ' + esc(moneyMinor(v.price)) + '</option>';
    });
    s += '</select>' + ICON.chevDown + '</label></div></div>';
    return s;
  }

  /* ============================================================
     POPUP SURFACE (opens on 'exon:cart:added')
     Zones: head → parent context band → intent-grouped offers →
     added tray (desktop) / footer deck (mobile) → trust → CTA row.
     ============================================================ */
  var Popup = (function () {
    var rootEl = el('[data-exon-popup]');
    if (!rootEl) return { open: function () {}, isOpen: function () { return false; } };
    var modal = el('[data-up-modal]', rootEl);
    var scrollZone = el('[data-up-scroll]', rootEl);
    var deckSlot = el('[data-up-deck-slot]', rootEl);
    var scrim = el('[data-up-scrim]', rootEl);
    var foot = el('[data-up-foot]', rootEl);
    var closeFn = null;
    var buyIntent = 'cart';       // 'cart' | 'checkout'
    var offerData = null;          // normalized response
    var offersByEl = {};           // dom id -> {offer, variants, sel}
    var liveProducts = {};         // handle -> product json (for selectors)
    var added = [];                // [{lineKey, variantId, productId, title, variantTitle, price(minor), image, upgrade, delta(minor)}]
    var deckOpen = false;
    var seq = 0;
    var addedVariantId = null;     // just-added variant → popup requests offers anchored on it only
    var swapPending = {};          // anchorVariantGid -> 1 while a swap of that anchor line is in flight
    var cartBound = false;         // the one exon:cart listener that keeps the CTA total live

    function open(detail) {
      if (!POPUP_ENABLED || !rootEl.hidden) return;
      buyIntent = (detail && detail.intent) === 'checkout' ? 'checkout' : 'cart';
      addedVariantId = detail && detail.addedVariantId;
      added = []; deckOpen = false; offerData = null; offersByEl = {}; liveProducts = {};
      renderParent(addedVariantId);
      renderContinue(true);
      if (!cartBound && window.EXON.bus) {
        cartBound = true;
        window.EXON.bus.on('exon:cart', function () { if (!rootEl.hidden) renderContinue(); });
      }
      scrollZone.innerHTML = '';
      deckSlot.innerHTML = '';
      modal.removeAttribute('data-no-offers');
      setScrim(false);

      GiftClaim.clearMsg(); GiftClaim.paint();
      rootEl.hidden = false;
      closeFn = window.EXON.overlays.open('exon-popup', { onClose: onClosed });

      /* fetch offers for the current cart; degrade to no-offers on failure */
      var mySeq = ++seq;
      loadOffers(mySeq);
    }
    function close() { if (closeFn) closeFn(); else hardHide(); }
    function onClosed() {
      /* dismiss telemetry: the popup closed with offers shown and nothing
         accepted (fires on X / backdrop / esc / CTA-without-add) */
      if (offerData && offerData.attributionToken && !added.length) {
        Events.track({
          event: 'dismiss', surface: 'popup',
          attribution_token: offerData.attributionToken,
          request_id: offerData.requestId || undefined
        });
      }
      Events.flush(true);
      DealClock.release(rootEl);
      rootEl.classList.add('closing');
      setTimeout(function () { rootEl.hidden = true; rootEl.classList.remove('closing'); }, 260);
      closeFn = null;
    }
    function hardHide() { rootEl.hidden = true; }

    function renderParent(addedVariantId) {
      var cart = window.EXON.cart.state;
      var item = pickAddedItem(cart, addedVariantId);
      var cv = el('[data-up-parent-cv]', rootEl);
      var nm = el('[data-up-parent-name]', rootEl);
      var sub = el('[data-up-parent-sub]', rootEl);
      if (!item) { if (cv) cv.innerHTML = ''; if (nm) nm.textContent = ''; if (sub) sub.textContent = ''; paintBand(null); return; }
      if (cv) cv.innerHTML = coverHtml((item.featured_image && item.featured_image.url) || item.image, item.product_title, true);
      if (nm) nm.textContent = item.product_title || item.title || '';
      var bits = [];
      if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
      bits.push(moneyMinor(item.final_line_price ? Math.round(item.final_line_price / (item.quantity || 1)) : item.final_price || item.price));
      if (sub) sub.textContent = bits.join(' · ');
      paintBand(item);
    }
    /* the product band as a folded xf row: cover, first part of the name,
       «in cart». Never throws; no controller on the page = nothing to paint. */
    function paintBand(item) {
      try {
        var ExonFold = window.ExonFold;
        var row = ExonFold && ExonFold.row('popup', 'band');
        if (!row) return;
        if (!item) { ExonFold.set(row, { hidden: true, title: '', icon: '' }); return; }
        var name = shortTitle(item.product_title || item.title);
        var img = safeImg((item.featured_image && item.featured_image.url) || item.image);
        ExonFold.set(row, {
          hidden: false,
          title: name,
          icon: img ? '<img src="' + esc(img) + '" alt="" loading="eager">' : 'gift',
          state: { kind: 'word', text: XT.in_cart },
          status: 'done'
        });
      } catch (e) {}
    }
    function pickAddedItem(cart, variantId) {
      var items = (cart && cart.items) || [];
      if (!items.length) return null;
      if (variantId) {
        var found = items.filter(function (it) { return String(it.variant_id || it.id) === String(variantId); })[0];
        if (found) return found;
      }
      return items[items.length - 1];
    }

    /* EXO-4667 P2: the ONE footer button. Its label follows the state:
       nothing added vs N added in this popup session, cart vs buy-now intent.
       The buy-now total is the LIVE cart's (exon-store refreshes state after
       every add/change), never an offer price. The click is proceed(). */
    function renderContinue(quiet) {
      var lock = el('[data-up-continue-lock]', rootEl);
      var lbl = el('[data-up-continue-label]', rootEl);
      var n = added.length;
      var checkout = buyIntent === 'checkout';
      var text;
      if (!n) text = checkout ? T.cont_none_checkout : T.cont_none_cart;
      else if (checkout) {
        var cart = window.EXON.cart.state;
        text = T.cont_added_checkout.replace('{t}', moneyMinor(cart && cart.total_price));
      } else text = n === 1 ? T.cont_added_cart_one : T.cont_added_cart.replace('{n}', String(n));
      if (lock) lock.hidden = !checkout;
      /* the state attribute is informational (the tick and the tests read
         it); the style no longer changes, the button is solid red always */
      var btn = el('[data-up-continue]', rootEl);
      if (btn) btn.setAttribute('data-up-cta-state', n ? 'added' : 'none');
      if (lbl && lbl.textContent !== text) {
        var was = lbl.textContent;
        lbl.textContent = text;
        /* the swap ticks once (a CSS animation restarted, no timer); not on
           the first paint of an open */
        if (was && !quiet && lbl.classList) {
          lbl.classList.remove('up-tick');
          void lbl.offsetWidth;
          lbl.classList.add('up-tick');
        }
      }
    }

    function loadOffers(mySeq) {
      var cart = window.EXON.cart.state;
      /* full cart + explicit anchor: the engine scores against the just-added
         variant only while excluding EVERYTHING already in the cart. Without a
         resolvable added variant this degrades to whole-cart scoring (fail-soft,
         same as the cart surface). */
      Offers.fetch('popup', cart, POPUP_LIMIT, addedVariantId).then(function (body) {
        if (mySeq !== seq || rootEl.hidden) return;
        var data = Offers.normalize(body);
        if (!data || (!data.groups.length && !data.offers.length)) {
          modal.setAttribute('data-no-offers', '');
          return;
        }
        return withLivePrices(data).then(function (data) {
          if (mySeq !== seq || rootEl.hidden) return;
          /* every offer lost its live price: nothing is shown, so nothing is
             stamped as seen */
          if (!data.groups.length && !data.offers.length) {
            modal.setAttribute('data-no-offers', '');
            return;
          }
          offerData = data;
          postOfferAttributes(data, 'popup');
          renderOffers(data);
        });
      }).catch(function () {
        if (mySeq === seq) modal.setAttribute('data-no-offers', '');
      });
    }

    function renderOffers(data) {
      var split = splitOffers(data, POPUP_LIMIT);
      var hidden = {};
      added.forEach(function (a) { if (a.productId) hidden['gid://shopify/Product/' + a.productId] = 1; });
      /* client-side in-cart exclusion: the popup now requests offers for the
         just-added variant only, so the engine can't drop offers for OTHER
         items already in the cart — filter them here by target variant. Never
         drop an upgrade anchored on the just-added line (upgrading the anchor
         is the whole point). Cheap + fail-soft (empty set → no exclusion). */
      var inCartVars = {};
      ((window.EXON.cart.state && window.EXON.cart.state.items) || []).forEach(function (it) {
        inCartVars[String(it.variant_id || it.id)] = 1;
      });
      /* primary and Game Pass companion both SWAP the same line — taking
         either removes both (M3 theme contract) */
      var takenAnchors = {};
      added.forEach(function (a) {
        if (a.swapped && a.offer && a.offer.anchorVariantGid) takenAnchors[a.offer.anchorVariantGid] = 1;
      });
      function keep(o) {
        if (hidden[o.productGid]) return false;
        if (isUpgrade(o) && o.anchorVariantGid && takenAnchors[o.anchorVariantGid]) return false;
        var anchoredOnAdded = isUpgrade(o) && o.anchorVariantGid && addedVariantId &&
          String(gidToId(o.anchorVariantGid)) === String(addedVariantId);
        if (anchoredOnAdded) return true;
        if (o.variantId && inCartVars[String(o.variantId)]) return false;
        return true;
      }
      var upgrades = split.upgrades.filter(keep);
      var comps = split.complementary.filter(keep);
      if (!upgrades.length && !comps.length) {
        /* nothing left to offer (e.g. the only anchor was just swapped): the
           previous cards must leave the DOM — a stale sibling upgrade left
           clickable would add a second, separately charged item. The added
           tray stays. */
        modal.setAttribute('data-no-offers', '');
        scrollZone.innerHTML = trayHtml();
        return;
      }
      modal.removeAttribute('data-no-offers');

      var html = '';
      /* each upgrade carries its OWN section label (edition, then subscription
         for the Game Pass companion); consecutive upgrades sharing a label
         share one section */
      var lastLabel = null;
      upgrades.forEach(function (o, i) {
        var label = upgradeSectionLabel(o);
        if (label !== lastLabel) {
          if (lastLabel !== null) html += '</div>';
          html += '<span class="up-sec-l">' + esc(label) + '</span><div class="up-body" data-up-group="upgrade">';
          lastLabel = label;
        }
        html += offerCardHtml(o, i * 0.1 + 0.08, i + 1, data);
      });
      if (lastLabel !== null) html += '</div>';
      if (comps.length) {
        var compDelay = 0.14 + Math.max(0, upgrades.length - 1) * 0.1;
        html += '<span class="up-sec-l">' + esc(T.sec_complete) + '</span><div class="up-body" data-up-group="comp">';
        comps.forEach(function (o, i) { html += offerCardHtml(o, i * 0.1 + compDelay, upgrades.length + i + 1, data); });
        html += '</div>';
      }
      html += trayHtml();
      scrollZone.innerHTML = html;
      DealClock.watch(rootEl);
      hydrateSelectors();
      /* impression telemetry for the shown offers */
      upgrades.concat(comps).forEach(function (o) { trackOffer('impression', o); });
      /* generate missing version/franchise pitch copy for next views */
      Offers.ensureCopy(upgrades);
    }

    function offerCardHtml(o, delay, pos, data) {
      var id = 'upo-' + (o.variantId || o.handle || Math.random().toString(36).slice(2));
      /* pos = 1-based render position at paint time (upgrades then comps),
         carried into the accept's _exon_position property */
      offersByEl[id] = { offer: o, variants: null, sel: o.variantId, pos: pos || 0 };
      var up = isUpgrade(o);
      var gp = isGamePassInclude(o);
      /* one badge max, explicit > inclusion > co-purchase > applied discount;
         the reason line below owns the "why", never this slot */
      var badge = o.badge || '';
      if (!badge) {
        if (gp) badge = T.gp_badge;
        else if (o.reasonCode === 'bought_together') badge = T.bought_badge;
        else if (o.discount && num(o.discount.pct) > 0 && o.compareAt && o.compareAt > o.price) {
          badge = '-' + Math.round(num(o.discount.pct) * 100) + '%';
        }
      }
      var badgeCls = gp ? ' amber' : (up ? '' : ' teal');
      var priceHtml;
      var anchorMinor = up ? (o.anchorPrice != null ? Math.round(o.anchorPrice * 100) : anchorLineMinor(o)) : null;
      if (up && anchorMinor != null) {
        var deltaMinor = Math.round(o.price * 100) - anchorMinor;
        priceHtml = '<b class="' + deltaCls(deltaMinor) + '">' + esc(deltaText(deltaMinor)) + '</b>';
      } else {
        priceHtml = '<b>' + esc(moneyMajor(o.price)) + '</b>' +
          (o.compareAt && o.compareAt > o.price ? '<s>' + esc(moneyMajor(o.compareAt)) + '</s>' : '');
      }
      var s = '<div class="up-offer" data-up-offer="' + esc(id) + '" style="animation-delay:' + delay + 's">';
      s += '<span class="up-cv lg">' + coverHtml(o.image, o.title, true) + '</span>';
      s += '<div class="up-info">';
      if (badge) s += '<span class="up-badge' + badgeCls + '">' + esc(badge) + '</span>';
      s += '<b class="up-ot">' + esc(o.title) + '</b>';
      if (o.variantTitle && o.variantTitle !== 'Default Title') s += '<span class="up-od">' + esc(o.variantTitle) + '</span>';
      var rl = reasonLineHtml(o);
      if (rl) s += '<span class="up-reason">' + ICON.sparkles + '<i>' + rl + '</i></span>';
      s += dealLineHtml(o);
      /* the Drop compatibility line rides inside the info cell, so the card's
         grid areas and the accept control stay exactly where they were */
      s += dropComboNoteHtml(data, o);
      s += '</div>';
      s += '<div data-up-amod>' + offerAmod(o, null) + '</div>';
      s += '<div data-up-sels-mount></div>';
      s += '<div class="up-actions"><span class="up-price ltr" data-up-price>' + priceHtml + '</span>';
      s += '<button class="up-add" type="button" data-up-add>' + esc(up ? T.upgrade : T.add) + '</button></div>';
      s += '</div>';
      return s;
    }

    /* for multi-variant offers, pull live product JSON to render the
       selector + refresh the amber panel/price from real variant data */
    function hydrateSelectors() {
      Object.keys(offersByEl).forEach(function (id) {
        var rec = offersByEl[id];
        var o = rec.offer;
        if (!o.handle) return;
        /* inclusion offers pin their engine-chosen plan/term — no selector */
        if (isGamePassInclude(o)) return;
        Products.get(o.handle).then(function (prod) {
          if (!prod || rootEl.hidden) return;
          var variants = (prod.variants || []).filter(function (v) { return v.available !== false; });
          if (variants.length < 2) return;
          rec.variants = variants;
          liveProducts[o.handle] = prod;
          var card = el('[data-up-offer="' + CSS.escape(id) + '"]', rootEl);
          if (!card) return;
          var sel = variants.filter(function (v) { return String(v.id) === String(rec.sel); })[0] || variants[variants.length - 1];
          rec.sel = sel.id;
          var mount = el('[data-up-sels-mount]', card);
          if (mount) mount.innerHTML = selectorHtml(variants, rec.sel);
          refreshCard(card, rec, sel);
        }).catch(function () { /* selector just won't render */ });
      });
    }
    function refreshCard(card, rec, variant) {
      syncDealLine(card, rec.offer, variant.id);
      var amod = el('[data-up-amod]', card);
      if (amod) amod.innerHTML = offerAmod(rec.offer, variant);
      var price = el('[data-up-price]', card);
      if (price) {
        var up = isUpgrade(rec.offer);
        var anchorMinor = up ? (rec.offer.anchorPrice != null ? Math.round(rec.offer.anchorPrice * 100) : anchorLineMinor(rec.offer)) : null;
        if (up && anchorMinor != null) {
          var deltaMinor = variant.price - anchorMinor;
          price.innerHTML = '<b class="' + deltaCls(deltaMinor) + '">' + esc(deltaText(deltaMinor)) + '</b>';
        } else {
          price.innerHTML = '<b>' + esc(moneyMinor(variant.price)) + '</b>' +
            (variant.compare_at_price && variant.compare_at_price > variant.price ? '<s>' + esc(moneyMinor(variant.compare_at_price)) + '</s>' : '');
        }
      }
    }

    /* -------- desktop added tray -------- */
    function trayHtml() {
      if (!added.length) return '';
      var s = '<div class="up-tray"><div class="up-tray-div"><i></i><span>' + esc(T.tray) + ' · ' + added.length + '</span><i></i></div><div class="up-added">';
      added.forEach(function (a) {
        s += '<div class="up-trow" data-up-trow="' + esc(a.lineKey) + '">';
        s += '<span class="up-cv sm">' + coverHtml(a.image, a.title, true) + '</span>';
        s += '<b>' + esc(a.upgrade ? (T.upg_to + a.title) : a.title) + (a.variantTitle && a.variantTitle !== 'Default Title' ? ' · ' + esc(a.variantTitle) : '') + '</b>';
        s += '<span class="up-trp">';
        /* only a REAL swap shows delta framing — an upgrade that fell back to a
           plain add charges full price and must say so */
        if (a.swapped && a.delta != null) s += '<span class="' + deltaCls(a.delta) + '">' + esc(deltaText(a.delta)) + '</span>' + (a.delta > 0 ? '<em>' + esc(T.add_just) + '</em>' : '');
        else s += '<span class="ltr">' + esc(moneyMinor(a.price)) + '</span>';
        s += '</span>';
        s += '<span class="up-tradd">' + ICON.check + ' ' + esc(T.added) + '</span>';
        s += '<button class="up-trx" type="button" data-up-trx="' + esc(a.lineKey) + '" title="' + esc(T.remove) + '">' + ICON.x + '</button>';
        s += '</div>';
      });
      s += '</div></div>';
      return s;
    }

    /* -------- mobile footer deck (card-stack) -------- */
    var CH = 66, GAP = 8, PEEK = 9;
    function renderDeck() {
      renderContinue();
      if (!added.length) { deckSlot.innerHTML = ''; foot.classList.remove('deck-open'); return; }
      /* swap-applied upgrades charge only their delta — the deck total must
         agree with the cart, not double-count the replaced line */
      var total = added.reduce(function (s, a) { return s + (a.swapped && a.delta != null ? a.delta : a.price); }, 0);
      var peek = Math.min(2, added.length - 1);
      var h = deckOpen ? added.length * (CH + GAP) - GAP : CH + peek * PEEK;
      var s = '<div class="udeck-wrap">';
      if (deckOpen) s += '<button class="udeck-close" type="button" data-up-deck-close>' + esc(T.close) + ' ' + ICON.chevDown + '</button>';
      s += '<div class="udeck" style="height:' + h + 'px">';
      added.forEach(function (a, i) {
        var front = i === added.length - 1;
        var depth = added.length - 1 - i;
        var style;
        if (deckOpen) style = 'bottom:' + (depth * (CH + GAP)) + 'px;z-index:' + (100 - depth) + ';';
        else style = 'bottom:0;z-index:' + (100 - depth) + ';transform:translateY(' + (-Math.min(depth, 2) * PEEK) + 'px) scale(' + (1 - Math.min(depth, 2) * 0.035) + ') translateX(var(--udc-dx));opacity:' + (depth > 2 ? 0 : 1) + ';';
        s += '<div class="udc" data-up-udc="' + esc(a.lineKey) + '" style="' + style + '">';
        s += '<span class="udc-cv">' + coverHtml(a.image, a.title) + '</span>';
        var rowPrice = (a.swapped && a.delta != null)
          ? '<i class="' + deltaCls(a.delta) + '">' + esc(deltaText(a.delta)) + '</i>'
          : '<i class="ltr">' + esc(moneyMinor(a.price)) + '</i>';
        s += '<span class="udc-in"><b>' + esc(a.title) + (a.variantTitle && a.variantTitle !== 'Default Title' ? ' · ' + esc(a.variantTitle) : '') + '</b>' + rowPrice + '</span>';
        if (front && !deckOpen) s += '<span class="udc-chip ltr">' + added.length + ' · ' + esc(moneyMinor(total)) + '</span>' + '<span class="udc-chev">' + ICON.chevUp + '</span>';
        if (deckOpen) s += '<button class="udc-x" type="button" data-up-udc-x="' + esc(a.lineKey) + '">' + ICON.x + '</button>';
        s += '</div>';
      });
      s += '</div></div>';
      deckSlot.innerHTML = s;
      foot.classList.toggle('deck-open', deckOpen && !!added.length);
    }
    function setScrim(on) {
      if (!scrim) return;
      scrim.classList.toggle('on', on && !!added.length);
      scrim.setAttribute('aria-hidden', on ? 'false' : 'true');
    }

    /* -------- add / remove offers from THIS popup session -------- */
    function addOffer(rec, cardEl) {
      var o = rec.offer;
      var variant = rec.variants ? rec.variants.filter(function (v) { return String(v.id) === String(rec.sel); })[0] : null;
      var variantId = variant ? variant.id : (o.variantId || rec.sel);
      if (!variantId) return;
      var priceMinor = variant ? variant.price : Math.round(o.price * 100);
      var up = isUpgrade(o);
      /* one swap per anchor line: while a swap of this anchor is in flight, or
         once one has been taken, every other upgrade of the same line is
         refused — it must never reach the plain-add branch below */
      var anchorGid = up ? o.anchorVariantGid : '';
      if (anchorGid && (swapPending[anchorGid] || anchorTaken(anchorGid))) return;
      /* an advertised swap is a REAL swap when its anchor line is in the cart:
         cart.upgrade replaces that line (drawer-accept parity, incl. the
         _original_variant undo marker). No resolvable anchor line — and every
         cross-sell — stays a plain add, EXCEPT an explicit cart_action swap:
         a swap never degrades into an add. */
      var swapLine = up ? anchorLine(o) : null;
      if (!swapLine && o.cartAction === 'swap') return;
      /* delta for a real swap comes from the LIVE line price — the tray and
         total must agree with what the cart actually charges */
      var anchorMinor;
      if (swapLine) anchorMinor = num(swapLine.final_price != null ? swapLine.final_price : swapLine.price);
      else anchorMinor = (up && o.anchorPrice != null) ? Math.round(o.anchorPrice * 100) : null;

      if (cardEl) cardEl.classList.add('choosing');
      var addBtn = cardEl && el('[data-up-add]', cardEl);
      if (addBtn) addBtn.setAttribute('aria-busy', 'true');

      /* accept attribution rides the cart line itself (finance ingestion) */
      var props = offerAcceptProps(o, {
        requestId: offerData && offerData.requestId,
        attributionToken: offerData && offerData.attributionToken,
        surface: 'popup',
        variantId: variantId,
        position: rec.pos
      });
      var mutation;
      /* a real swap locks the anchor before the first request: the sibling
         upgrade cards go non-actionable now, and stay so until the re-render
         drops them (success) or this swap fails (released below) */
      var lockGid = swapLine ? anchorGid : '';
      if (lockGid) { swapPending[lockGid] = 1; lockAnchorSiblings(lockGid, cardEl, true); }
      if (swapLine) {
        /* pre-stamp the outgoing line's snapshot so cart.upgrade carries the
           attribution onto the target add (then stamps _original_variant last) */
        stampCarriedProps(swapLine.key, props);
        mutation = window.EXON.cart.upgrade(swapLine.key, Number(variantId), swapLine.quantity || 1, swapLine.variant_id || swapLine.id);
      } else {
        var addItem = { id: Number(variantId), quantity: 1 };
        if (Object.keys(props).length) addItem.properties = props;
        mutation = window.EXON.cart.add(addItem, { silent: true });
      }
      mutation.then(function (cart) {
        var line = lineForVariant(cart, variantId, swapLine ? (swapLine.variant_id || swapLine.id) : null);
        /* the tray and the total print what the cart charges: the returned
           line's unit price — but only when that line is unambiguously OURS:
           the swap line carrying our undo stamp, or the one cart line of this
           variant. With several lines of the variant the match may be an
           older, differently priced line, so it falls back to the live
           product price, as it does when the cart has no such line */
        var sameVariant = ((cart && cart.items) || []).filter(function (it) { return String(it.variant_id || it.id) === String(variantId); }).length;
        var ours = !!line && (sameVariant === 1 ||
          (!!swapLine && !!line.properties && line.properties._original_variant === String(swapLine.variant_id || swapLine.id)));
        var lineMinor = ours ? (line.final_price != null ? line.final_price : line.price) : null;
        var paidMinor = (typeof lineMinor === 'number' && isFinite(lineMinor)) ? lineMinor : priceMinor;
        var delta = anchorMinor != null ? paidMinor - anchorMinor : null;
        added.push({
          lineKey: line ? line.key : String(variantId),
          variantId: variantId,
          productId: line ? line.product_id : gidToId(o.productGid),
          title: o.title,
          variantTitle: variant ? (variant.public_title || variant.title) : o.variantTitle,
          price: paidMinor,
          image: (line && line.featured_image && line.featured_image.url) || o.image,
          upgrade: up,
          swapped: !!swapLine, /* real swap — totals count the delta, X reverts */
          delta: delta,
          offer: o /* kept so removing an added item can fire a `decline` */
        });
        /* the swapped entry now marks the anchor taken (anchorTaken) */
        if (lockGid) delete swapPending[lockGid];
        /* a successful accept fires BOTH accept and add_to_cart (extension parity) */
        trackOffer('accept', o, variantId);
        trackOffer('add_to_cart', o, variantId);
        /* after the choose-pop, re-render offers (hides the added one) + tray + deck */
        setTimeout(function () {
          if (offerData) renderOffers(offerData);
          renderDeck();
        }, 380);
      }).catch(function () {
        if (cardEl) cardEl.classList.remove('choosing');
        if (addBtn) addBtn.removeAttribute('aria-busy');
        if (lockGid) { delete swapPending[lockGid]; lockAnchorSiblings(lockGid, cardEl, false); }
      });
    }
    /* has this popup session already swapped the line anchored on `gid`? */
    function anchorTaken(gid) {
      return added.some(function (a) { return a.swapped && a.offer && a.offer.anchorVariantGid === gid; });
    }
    /* (un)lock every OTHER painted upgrade card of the same anchor line */
    function lockAnchorSiblings(gid, exceptCard, on) {
      els('[data-up-offer]', scrollZone).forEach(function (card) {
        if (card === exceptCard) return;
        var rec = offersByEl[card.getAttribute('data-up-offer')];
        if (!rec || !isUpgrade(rec.offer) || rec.offer.anchorVariantGid !== gid) return;
        var btn = el('[data-up-add]', card);
        if (!btn) return;
        if (on) { btn.setAttribute('disabled', ''); btn.setAttribute('aria-disabled', 'true'); }
        else { btn.removeAttribute('disabled'); btn.removeAttribute('aria-disabled'); }
      });
    }
    function removeAdded(lineKey) {
      var a = added.filter(function (x) { return String(x.lineKey) === String(lineKey); })[0];
      if (!a) return;
      var cart = window.EXON.cart.state;
      var line = (cart && cart.items || []).filter(function (it) { return String(it.key) === String(a.lineKey); })[0];
      var p;
      if (a.swapped && line && line.properties && line.properties._original_variant) {
        /* the line REPLACED the customer's original purchase — X must give the
           original back (drawer-undo revert), never delete the product */
        p = window.EXON.cart.revert(a.lineKey, line.quantity, line.properties._original_variant);
      } else {
        p = (line && line.quantity > 1)
          ? window.EXON.cart.change(a.lineKey, line.quantity - 1)
          : window.EXON.cart.change(a.lineKey, 0);
      }
      p.then(function () {
        if (a.offer) trackOffer('decline', a.offer, a.variantId);
        added = added.filter(function (x) { return x !== a; });
        if (!added.length) { deckOpen = false; setScrim(false); }
        if (offerData) renderOffers(offerData);
        renderDeck();
      }).catch(function () {});
    }
    /* the cart line for a variant; origVariantId (swap accepts) prefers OUR
       just-swapped line by its undo stamp, so a pre-existing plain line with
       the same variant can never claim the tray entry */
    function lineForVariant(cart, variantId, origVariantId) {
      var matches = ((cart && cart.items) || []).filter(function (it) { return String(it.variant_id || it.id) === String(variantId); });
      if (origVariantId != null) {
        for (var i = 0; i < matches.length; i++) {
          var p = matches[i].properties;
          if (p && p._original_variant === String(origVariantId)) return matches[i];
        }
      }
      return matches.pop();
    }

    /* -------- continue (the one footer button) -------- */
    function proceed() {
      Events.flush(false);
      close();
      if (buyIntent === 'checkout') { GiftClaim.beforeCheckout().then(function () { window.location = '/checkout'; }); }
      else { setTimeout(function () { Drawer.open(); }, 120); }
    }

    /* -------- telemetry helper -------- */
    function trackOffer(event, o, variantId) {
      if (!offerData || !offerData.attributionToken) return;
      Events.track({
        event: event,
        surface: 'popup',
        attribution_token: offerData.attributionToken,
        request_id: offerData.requestId || undefined,
        target_id: o.targetId || undefined,
        /* THE LEARNING KEYS (EXO-2254). offers.record_offer_event writes
           offer_selection_stats ONLY when the event metadata carries BOTH
           `axis` and a selection arm. Without them the row is dropped
           silently — no error, nothing anywhere reports a failure — which is
           how this loop sat frozen for 47 days: the severed edge between two
           healthy halves is where nobody puts a probe.

           🔴 `axis` IS snake_case ONLY. Do not "tidy" it to `Axis`, `offerAxis`
           or camelCase. Its neighbours mislead you here: the gate reads
           `selection_arm_id` OR `selectionArmId`, and genre_bucket /
           anchor_variant_gid are dual-spelling too. `axis` is NOT the only
           single-spelling read though — `decision_signal` and `relevance_tier`
           have no camelCase arm either, and decision_signal unlocks the
           soft-prior branch in this same stats block, so you will meet it.
           Treat dual-spelling as the thing to check, never to assume.
           Verified against the newest IN-REPO function definition; the
           production body is not confirmed from here.

           FOUR KEYS, NOT TWO, AND THE OTHER TWO ARE NOT DECORATION.
           offer_selection_stats is keyed on SIX columns — (shop_domain,
           surface, anchor_vendor_bucket, genre_bucket, axis, arm_id). Send
           only axis + arm and the other two fall to their defaults 'all' and
           'other', so the row is WRITTEN but filed where nothing reads: the
           scorers look up the anchor's REAL buckets, and the finance
           realized-margin rollup keys on the same six taken from the ORDER
           rail, where the true values ARE present — so the two halves of one
           arm would never join. Measured in production: all 42 engine-lane
           events sit in the single ('all','other') group, while the 41
           surviving pre-freeze rows carry real buckets.

           The two are NOT symmetric, which is the trap inside the trap:
             · anchor_variant_gid — the server RESOLVES anchor_vendor_bucket
               from it (repair branch: if the bucket is still 'all' and the
               gid is present, it looks the anchor up). Send the gid, not the
               bucket.
             · genre_bucket — HAS NO REPAIR BRANCH. Its only assignment is
               from metadata, defaulting to 'other'. If we do not send it,
               nothing server-side ever fills it.

           🔴 `|| undefined` RATHER THAN `|| ''` IS LOAD-BEARING — DO NOT
           "SIMPLIFY" IT. The dual-spelling reads are
           nullif(btrim(coalesce(snake, camel)), ''), and COALESCE TAKES THE
           FIRST NON-NULL — an empty string IS non-null. So `''` does not
           merely fail like an absent key: it SHADOWS a valid camelCase value
           that would otherwise have been read, and the event is dropped.
           Omitted when empty; present only when real. */
        metadata: {
          product_gid: o.productGid || '',
          target_variant_gid: variantId ? 'gid://shopify/ProductVariant/' + variantId : (o.variantGid || ''),
          offer_type: o.offerType || o.tier || '',
          axis: o.axis || undefined,
          selection_arm_id: o.selectionArmId || undefined,
          genre_bucket: o.genreBucket || undefined,
          anchor_variant_gid: o.anchorVariantGid || undefined
        }
      });
    }

    /* -------- events (delegated) -------- */
    rootEl.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-up-close]')) { e.preventDefault(); return close(); }
      if (t === rootEl) return close();
      if (t.closest('[data-up-continue]')) { e.preventDefault(); return proceed(); }
      if (t.closest('[data-up-scrim]')) { deckOpen = false; setScrim(false); renderDeck(); return; }
      if (t.closest('[data-up-deck-close]')) { deckOpen = false; setScrim(false); renderDeck(); return; }

      var addBtn = t.closest('[data-up-add]');
      if (addBtn) {
        e.preventDefault();
        var card = addBtn.closest('[data-up-offer]');
        var rec = card && offersByEl[card.getAttribute('data-up-offer')];
        if (rec && !card.classList.contains('choosing')) addOffer(rec, card);
        return;
      }
      var trx = t.closest('[data-up-trx]');
      if (trx) { e.preventDefault(); return removeAdded(trx.getAttribute('data-up-trx')); }
      var udcX = t.closest('[data-up-udc-x]');
      if (udcX) { e.preventDefault(); return removeAdded(udcX.getAttribute('data-up-udc-x')); }
      /* tap a collapsed deck to fan out (a completed swipe must not read as a tap) */
      var udc = t.closest('[data-up-udc]');
      if (udc && Date.now() - justDragged < 350) return;
      if (udc && !deckOpen && !t.closest('[data-up-udc-x]')) { deckOpen = true; setScrim(true); renderDeck(); return; }
    });
    /* variant selector change */
    rootEl.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-up-sel]');
      if (!sel) return;
      var card = sel.closest('[data-up-offer]');
      var rec = card && offersByEl[card.getAttribute('data-up-offer')];
      if (!rec || !rec.variants) return;
      rec.sel = sel.value;
      var variant = rec.variants.filter(function (v) { return String(v.id) === String(rec.sel); })[0];
      if (variant) refreshCard(card, rec, variant);
    });
    /* deck swipe-to-remove — app-grade physics: 1:1 tracking with a slight
       tilt + fade while dragging, velocity-aware dismissal (a quick flick
       removes even on a short travel), an animated fling OFF-SCREEN before
       the model updates, and an overshooting spring back home on release. */
    var justDragged = 0;
    wireDeckDrag();
    function deckDragPaint(udc, dx) {
      udc.style.setProperty('--udc-dx', dx + 'px');
      udc.style.transform = 'translateX(' + dx + 'px) rotate(' + (dx * 0.028) + 'deg)';
      udc.style.opacity = String(Math.max(0.55, 1 - Math.abs(dx) / 320));
    }
    function wireDeckDrag() {
      var drag = null;
      deckSlot.addEventListener('pointerdown', function (e) {
        var udc = e.target.closest('[data-up-udc]');
        if (!udc || !deckOpen || e.target.closest('[data-up-udc-x]')) return;
        drag = {
          udc: udc, x: e.clientX, key: udc.getAttribute('data-up-udc'),
          lastX: e.clientX, lastT: e.timeStamp, v: 0, moved: false
        };
        udc.classList.add('dragging');
        try { udc.setPointerCapture(e.pointerId); } catch (er) {}
      });
      deckSlot.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (Math.abs(dx) > 6) drag.moved = true;
        /* exponentially-smoothed velocity (px/ms) for flick detection */
        var dt = Math.max(1, e.timeStamp - drag.lastT);
        drag.v = 0.75 * ((e.clientX - drag.lastX) / dt) + 0.25 * drag.v;
        drag.lastX = e.clientX; drag.lastT = e.timeStamp;
        deckDragPaint(drag.udc, dx);
      });
      function end(e) {
        if (!drag) return;
        var d = drag; drag = null;
        var dx = e.clientX - d.x;
        d.udc.classList.remove('dragging');
        if (d.moved) justDragged = Date.now();
        var flick = Math.abs(d.v) > 0.55 && (d.v > 0) === (dx > 0);
        if (Math.abs(dx) > 90 || (flick && Math.abs(dx) > 24)) {
          /* commit: fling the card out in its travel direction, then update */
          var dir = dx >= 0 ? 1 : -1;
          d.udc.classList.add('dismissing');
          d.udc.style.transform =
            'translateX(' + dir * Math.max(window.innerWidth, 480) + 'px) rotate(' + dir * 9 + 'deg)';
          var done = false;
          var finish = function () { if (done) return; done = true; removeAdded(d.key); };
          d.udc.addEventListener('transitionend', finish, { once: true });
          setTimeout(finish, 340); /* fallback: reduced-motion / lost event */
        } else {
          /* spring home with a small overshoot, then hand back to the deck */
          d.udc.classList.add('springback');
          d.udc.style.setProperty('--udc-dx', '0px');
          d.udc.style.transform = '';
          d.udc.style.opacity = '';
          setTimeout(function () { d.udc.classList.remove('springback'); renderDeck(); }, 500);
        }
      }
      deckSlot.addEventListener('pointerup', end);
      deckSlot.addEventListener('pointercancel', end);
    }

    return { open: open, close: close, isOpen: function () { return rootEl && !rootEl.hidden; } };
  })();

  /* ============================================================
     CART DRAWER SURFACE
     Renders from EXON.cart.state (/cart.js source of truth). Goal bar,
     line items + qty steppers + הסרה, inline amber upgrade panels with
     undo-revert, rec carousel, warranty bump, reviews, footer.
     ============================================================ */
  var Drawer = (function () {
    var rootEl = el('[data-exon-drawer]');
    if (!rootEl) return { open: function () {}, isOpen: function () { return false; } };
    var body = el('[data-cd-body]', rootEl);
    var linesMount = el('[data-cd-lines]', rootEl);
    var recsSlot = el('[data-cd-recs-slot]', rootEl);
    var warrEl = el('[data-cd-warr]', rootEl);
    var closeFn = null;
    var offerData = null;
    var recState = {};   // handle -> {variants, sel, product}
    /* The live IntersectionObserver for the rec cards. Held so a re-render can
       disconnect the previous one: renderRecs replaces recsSlot.innerHTML, which
       detaches every observed card, and observation survives detachment until
       something calls unobserve/disconnect. */
    var recIO = null;
    /* Bumped whenever the rec cards are torn down. An observer's callback closes
       over the generation it was built for and returns if it is no longer current:
       disconnect() stops future observations but does NOT drop entries already
       queued for delivery (only takeRecords() does), so a callback from a previous
       render can still fire — with geometry snapshotted before the teardown. */
    var recGen = 0;
    /* How much of an offer card must be visible before it counts as SEEN. In
       PIXELS of clip-aware height — see the note at the intersection callback for
       why a fraction was wrong here. 120 sits comfortably under the 176px this
       container can actually expose (measured), so it is reachable for the
       tallest card the rail renders, with margin. */
    var SEEN_MIN_PX = 120;
    /* A ladder of thresholds so the observer keeps reporting as exposure grows. */
    var SEEN_STEPS = (function () {
      var t = [], i = 0;
      for (; i <= 20; i++) t.push(i / 20);
      return t;
    })();
    /* Two offers land on the SAME offer_selection_stats row when this tuple
       matches — the identity that decides whether an impression is a repeat.
       Compared FIELD BY FIELD on purpose: joining them into one string needs a
       delimiter, and any delimiter that can also occur INSIDE a value silently
       merges two distinct rows into one. That would present as a suppressed
       impression — the exact failure class this round exists to remove. Field
       comparison cannot collide and needs no escaping. */
    /* The identity of the offer itself, degrading through the fields that can
       stand in for a handle. An offer with NO handle is not exempt from dedupe:
       skipping it entirely would let it re-emit on every render, which is the
       inflation direction — the failure this round exists to prevent. Two offers
       with no usable identity at all compare EQUAL and therefore dedupe together;
       that can under-count an unidentifiable offer, and under-counting is the
       side to err on when the alternative is a silently inflated denominator. */
    function offerIdentity(x) {
      return x.handle || x.variantGid || x.productGid || x.targetId || '';
    }
    /* ⚖️ THE COMPARATOR MUST COVER EVERY COLUMN OF THE ROW KEY, and the contract
       is written down in this same file — see trackRec's note: offer_selection_stats
       is keyed on SIX columns (shop_domain, surface, anchor_vendor_bucket,
       genre_bucket, axis, arm_id). Two are fixed here (one shop, surface 'cart'),
       so FOUR must be compared. Comparing only axis + arm merged genuinely
       distinct rows, and the suppressed one then collected accepts against an
       impression count nothing raised — the defect this round exists to remove,
       found three separate times in three different dimensions because each pass
       fixed the instance in front of it instead of checking against this contract
       wholesale. If a column is ever added to the key, add it HERE.

       anchor_vendor_bucket is compared via `anchorVariantGid`, which is what the
       client actually holds — the server resolves the bucket from it. That is
       FINER-GRAINED than the row key: two different anchors can resolve to one
       bucket, so this can allow two impressions where the server keeps one row.
       Deliberate. Those are two distinct offers a customer actually saw, and the
       server aggregates them; the opposite error would suppress a real view. */
    function sameStatsRow(a, b) {
      return offerIdentity(a) === offerIdentity(b) &&
             (a.axis || '') === (b.axis || '') &&
             (a.selectionArmId || '') === (b.selectionArmId || '') &&
             (a.anchorVariantGid || '') === (b.anchorVariantGid || '') &&
             (a.genreBucket || '') === (b.genreBucket || '');
    }
    var lineUpgrades = {}; // line key -> the upgrade offers painted on it (edition first, then Game Pass)
    var upgradePending = {}; // line key -> 1 while that line's swap is in flight (per line, never global)
    var warrantyBusy = false;
    var seq = 0;

    function open() {
      if (!DRAWER_ENABLED || !rootEl.hidden) return;
      rootEl.hidden = false;
      /* entrance-only content stagger: stamped for the opening cascade, then
         removed so re-renders (qty, offer paints) never re-animate lines */
      var panel = el('.cart-drawer', rootEl);
      if (panel) {
        panel.classList.add('just-opened');
        setTimeout(function () { panel.classList.remove('just-opened'); }, 700);
      }
      closeFn = window.EXON.overlays.open('exon-drawer', { onClose: onClosed });
      render();
      var mySeq = ++seq;
      loadRecs(mySeq);
    }
    function close() { if (closeFn) closeFn(); else rootEl.hidden = true; }
    function onClosed() {
      DealClock.release(rootEl);
      rootEl.classList.add('closing');
      setTimeout(function () { rootEl.hidden = true; rootEl.classList.remove('closing'); }, 520);
      closeFn = null;
    }

    /* re-render on cart changes while open (guard bus access the same way
       boot() does, so a script-order change can't throw at construct time) */
    (function wireCartBus() {
      if (!window.EXON || !window.EXON.bus) { return setTimeout(wireCartBus, 40); }
      window.EXON.bus.on('exon:cart', function () { if (!rootEl.hidden) render(); });
    })();

    function render() {
      var cart = window.EXON.cart.state;
      if (!cart) return;
      renderCount(cart);
      renderGoal(cart);
      try { GoalRow.paint(); } catch (e) {}
      renderLines(cart);
      renderSubtotal(cart);
      syncWarranty(cart);
    }

    function renderCount(cart) {
      var c = el('[data-cd-count]', rootEl);
      if (c) c.textContent = cart.item_count;
    }
    function renderSubtotal(cart) {
      var s = el('[data-cd-subtotal]', rootEl);
      if (s) s.textContent = moneyMinor(cart.total_price);
    }

    function renderGoal(cart) {
      var goal = el('[data-cd-goal]', rootEl);
      if (!goal) return;
      /* the gift goal (shop metafield exon.gift_offer) replaces the store goal
         while its window is open (same target + basis the claim uses);
         otherwise this is the store goal on cart.total_price, unchanged */
      var ag = activeGoal(cart);
      var target = ag.target;
      if (!(target > 0)) { goal.hidden = true; return; }
      var sub = ag.basis;
      var pct = Math.min(100, (sub / target) * 100);
      var remaining = Math.max(0, target - sub);
      var done = remaining === 0;
      var reward = ag.reward;
      goal.classList.toggle('done', done);
      var fill = el('[data-cd-goal-fill]', goal);
      if (fill) fill.style.width = pct + '%';
      var ic = el('[data-cd-goal-ic]', goal);
      if (ic) ic.innerHTML = done
        ? '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>'
        : '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';
      var txt = el('[data-cd-goal-text]', goal);
      if (txt) {
        if (done) txt.innerHTML = HE
          ? '<b>' + esc(T.goal_ic_done) + '</b> ' + esc(reward) + ' 🎁'
          : '<b>' + esc(T.goal_ic_done) + '</b> ' + esc(reward) + ' 🎁';
        else if (ag.gift) txt.innerHTML = HE
          ? 'עוד <b class="ltr">' + esc(moneyMinor(remaining)) + '</b> ל-<b>' + esc(reward) + '</b> 🎁'
          : '<b class="ltr">' + esc(moneyMinor(remaining)) + '</b> more for <b>' + esc(reward) + '</b> 🎁';
        else txt.innerHTML = HE
          ? 'עוד <b class="ltr">' + esc(moneyMinor(remaining)) + '</b> ותקבלו <b>' + esc(reward) + '</b> 🎁'
          : '<b class="ltr">' + esc(moneyMinor(remaining)) + '</b> to go for <b>' + esc(reward) + '</b> 🎁';
      }
    }

    function renderLines(cart) {
      if (!linesMount) return;
      var items = cart.items || [];
      var html = items.map(lineHtml).join('');
      linesMount.innerHTML = html;
    }
    function lineHtml(it) {
      var props = it.properties || {};
      var orig = props._original_variant;
      var img = it.featured_image && it.featured_image.url;
      var s = '<div class="cd-line" data-cd-line="' + esc(it.key) + '">';
      if (orig) s += '<button class="cd-undo" type="button" data-cd-undo="' + esc(it.key) + '" title="' + esc(T.undo) + '">' + ICON.rotate + '</button>';
      s += '<div class="cd-main"><span class="up-cv">' + coverHtml(img, it.product_title, true) + '</span>';
      s += '<div class="cd-li"><b>' + esc(it.product_title) + '</b>';
      if (it.variant_title && it.variant_title !== 'Default Title') s += '<span>' + esc(it.variant_title) + '</span>';
      s += '<div class="cd-row"><span class="cd-qty ltr">';
      s += '<button type="button" data-cd-qty-dn="' + esc(it.key) + '">' + ICON.minus + '</button>';
      s += '<span>' + it.quantity + '</span>';
      s += '<button type="button" data-cd-qty-up="' + esc(it.key) + '">' + ICON.plus + '</button></span>';
      s += '<span class="cd-lend"><button class="cd-lrm" type="button" data-cd-remove="' + esc(it.key) + '">' + esc(T.remove) + '</button>';
      s += '<span class="cd-price ltr"><b>' + esc(moneyMinor(it.final_line_price != null ? it.final_line_price : it.line_price)) + '</b>';
      if (it.original_line_price && it.original_line_price > it.final_line_price) s += '<s>' + esc(moneyMinor(it.original_line_price)) + '</s>';
      s += '</span></span></div></div></div>';
      /* inline amber upgrade panel for this line, if the offers API gave one */
      s += '<div data-cd-upg="' + esc(it.key) + '"></div>';
      s += '</div>';
      return s;
    }

    /* fetch offers → paint per-line upgrade panels + the rec carousel */
    function loadRecs(mySeq) {
      var cart = window.EXON.cart.state;
      Offers.fetch('cart', cart, CART_LIMIT).then(function (body) {
        if (mySeq !== seq || rootEl.hidden) return;
        var data = Offers.normalize(body);
        if (!data) { offerData = data; renderRecs([]); return; }
        return withLivePrices(data).then(function (data) {
          if (mySeq !== seq || rootEl.hidden) return;
          offerData = data;
          /* nothing left to show is not stamped as seen */
          if (data.groups.length || data.offers.length) postOfferAttributes(data, 'cart');
          paintLineUpgrades(data);
          var split = splitOffers(data, CART_LIMIT);
          renderRecs(split.complementary, data);
          DealClock.watch(rootEl);
        });
      }).catch(function () { renderRecs([]); });
    }

    /* per-line upgrade panels: match upgrade offers to a cart line by its
       anchor variant (the line it upgrades FROM). R1: up to TWO panels per
       line — the first non-Game-Pass upgrade (edition, version, tier…) then
       the Game Pass inclusion in its compact form (the engine's companion). */
    function paintLineUpgrades(data) {
      var cart = window.EXON.cart.state;
      var upgrades = (data.offers || []).filter(isUpgrade)
        .concat((data.groups || []).reduce(function (acc, g) {
          return acc.concat(g.offers.filter(isUpgrade));
        }, []));
      lineUpgrades = {};
      var items = cart.items || [];
      var used = {};
      /* match an upgrade offer to a line: exact anchor-variant match is
         authoritative; when an offer carries NO anchor_variant_gid, fall back
         to a product-id match (offer product vs the line's product) so a
         same-product edition/version upgrade still paints. `fallback` is the
         second pass so exact anchor matches are always preferred globally.
         `gamePass` picks the slot: each line holds at most one of each. */
      function matchIdx(it, fallback, gamePass) {
        var anchorGid = 'gid://shopify/ProductVariant/' + (it.variant_id || it.id);
        for (var i = 0; i < upgrades.length; i++) {
          if (used[i]) continue;
          var o = upgrades[i];
          if (isGamePassInclude(o) !== gamePass) continue;
          var hit = o.anchorVariantGid
            ? (o.anchorVariantGid === anchorGid)
            : (fallback && o.productGid && String(gidToId(o.productGid)) === String(it.product_id));
          if (hit) return i;
        }
        return -1;
      }
      var slots = {}; // line key -> { primary, gamePass }
      [false, true].forEach(function (fallback) {
        items.forEach(function (it) {
          if (it.properties && it.properties._original_variant) return; // already upgraded
          if (!el('[data-cd-upg="' + cssEsc(it.key) + '"]', rootEl)) return;
          var slot = slots[it.key] || (slots[it.key] = { primary: null, gamePass: null });
          [false, true].forEach(function (gamePass) {
            var name = gamePass ? 'gamePass' : 'primary';
            if (slot[name]) return;
            var idx = matchIdx(it, fallback, gamePass);
            if (idx < 0) return;
            used[idx] = 1;
            slot[name] = upgrades[idx];
          });
        });
      });
      items.forEach(function (it) {
        var mount = el('[data-cd-upg="' + cssEsc(it.key) + '"]', rootEl);
        var slot = slots[it.key];
        var list = slot ? [slot.primary, slot.gamePass].filter(Boolean) : [];
        if (list.length) lineUpgrades[it.key] = list; /* kept for upgrade telemetry */
        /* clear mounts for lines with no upgrade painted (or already upgraded) */
        if (mount) mount.innerHTML = list.map(function (o) { return upgradePanelHtml(it, o, data); }).join('');
      });
      /* generate missing version/franchise pitch copy for next views */
      Offers.ensureCopy(upgrades);
    }
    function upgradePanelHtml(it, o, data) {
      var deltaMinor;
      if (o.anchorPrice != null) deltaMinor = Math.round((o.price - o.anchorPrice) * 100);
      else deltaMinor = Math.round(o.price * 100) - (it.final_price || it.price);
      var value = T.upg_to + (o.editionLabel || o.variantTitle || o.title);
      var amod;
      if (isGamePassInclude(o)) {
        /* subscription-inclusion: same amber panel, compact drawer form */
        amod = gamepassAmod(o);
      } else if (isVersionUpgrade(o)) {
        /* game upgrade (e.g. GTA 5 → GTA 6): pitch copy, not edition delta */
        amod = versionAmod(o);
      } else if (o.perMonthMinor) {
        amod = amodValuePerMonth(T.your_mo, value, o.perMonthMinor, o.fromPerMonthMinor || 0, o.monthlyDropPct || 0, '');
      } else {
        var feats = o.featureDeltaDisplayable === false
          ? []
          : ((o.extras && o.extras.length ? o.extras : o.features) || []);
        amod = amodValue(T.unlock, value, feats.slice(0, 2));
      }
      var s = '<div class="cd-upg">' + amod + dealLineHtml(o) + dropComboNoteHtml(data, o);
      s += '<div class="cd-upg-foot"><span class="cd-upg-delta-wrap"><i class="cd-upg-delta ' + deltaCls(deltaMinor) + '">' + esc(deltaText(deltaMinor)) + '</i>' + (deltaMinor > 0 ? '<em class="cd-upg-sub">' + esc(T.add_once) + '</em>' : '') + '</span>';
      s += '<button class="cd-upbtn" type="button" data-cd-upgrade="' + esc(it.key) + '" data-cd-upg-variant="' + esc(o.variantId) + '" data-cd-upg-orig="' + esc(it.variant_id || it.id) + '">' + esc(T.upgrade) + '</button></div></div>';
      return s;
    }

    function renderRecs(offers, data) {
      if (!recsSlot) return;
      /* Every path below replaces or clears recsSlot, detaching whatever the
         previous observer was watching. Disconnect FIRST so stale targets and
         their detached subtrees are not retained. */
      if (recIO) { recIO.disconnect(); recIO = null; }
      recGen++;   /* invalidate any entries the old observer already queued */
      if (!offers || !offers.length) { recsSlot.innerHTML = ''; return; }
      var inCart = {};
      ((window.EXON.cart.state && window.EXON.cart.state.items) || []).forEach(function (it) { inCart[it.product_id] = 1; });
      var recs = offers.filter(function (o) { return !inCart[gidToId(o.productGid)]; }).slice(0, 4);
      if (!recs.length) { recsSlot.innerHTML = ''; return; }
      var s = '<div class="cd-rec"><span class="bb-label">' + esc(T.rec_title) + ' <i class="ai-pill">' + ICON.sparkles + ' ' + esc(T.rec_ai) + '</i></span><div class="cd-recs">';
      recs.forEach(function (o, i) {
        var id = 'crec-' + (o.variantId || o.handle);
        /* `seen` rides ON the recState entry (no parallel map) and is carried
           across the rebuild: renderRecs re-creates these objects, so a naive
           reassignment would forget which offers were already counted and
           re-count them on the next reopen.

           ⚠️ CARRY IT BY OFFER IDENTITY, NOT BY SLOT. The map key is
           'crec-' + (variantId || handle), so the SAME key can hold a DIFFERENT
           offer on the next response, and the SAME product can land under a
           different key when its variant changes. Keying `seen` to the slot
           therefore fails both ways: a new offer inherits a suppression it never
           earned, and a product already counted gets counted again under a new
           variant.

           🔑 THE IDENTITY IS THE STATS ROW, NOT THE PRODUCT. offer_selection_stats
           is keyed per (…, axis, arm_id), so the SAME product served under a
           different axis or selection arm is a DIFFERENT row that needs its own
           impression. Deduping on handle alone would suppress it, and that row
           would then collect accepts against an impression count nothing raised —
           precisely the defect this round exists to remove, reintroduced one
           dimension over. So compare the tuple that decides WHERE the event
           lands. Absent axis/arm compare equal, so the degenerate case collapses
           back to one-impression-per-product. */
        /* No handle gate: an offer without one still needs dedupe, and skipping
           it would re-emit on every render. sameStatsRow handles the fallback. */
        var alreadySeen = false;
        Object.keys(recState).forEach(function (k) {
          var pr = recState[k];
          if (pr && pr.seen && pr.offer && sameStatsRow(pr.offer, o)) alreadySeen = true;
        });
        recState[id] = {
          offer: o, variants: null, sel: o.variantId, pos: i + 1,
          seen: alreadySeen
        };
        s += '<div class="crec" data-cd-rec="' + esc(id) + '">';
        s += '<span class="crec-cv">' + coverHtml(o.image, o.title) + '</span>';
        s += '<b class="crec-t">' + esc(o.title) + '</b>';
        var rl = reasonLineHtml(o) || esc(o.reason || '');
        if (rl) s += '<span class="crec-r">' + ICON.sparkles + ' <span>' + rl + '</span></span>';
        s += dealLineHtml(o);
        s += '<div data-cd-rec-sel></div>';
        s += dropComboNoteHtml(data, o);
        s += '<div class="crec-row"><b class="ltr" data-cd-rec-price>' + esc(moneyMajor(o.price)) + '</b>';
        s += '<button class="crec-add" type="button" data-cd-rec-add title="' + esc(T.add) + '">' + ICON.plus + '</button></div>';
        s += '</div>';
      });
      s += '</div></div>';
      recsSlot.innerHTML = s;
      hydrateRecSelectors();
      observeRecImpressions();
    }

    /**
     * Cart impression telemetry — ON VISIBILITY, NOT ON RENDER.
     *
     * 🔑 WHY THIS IS NOT A COPY OF THE POPUP'S ONE-LINER. The popup fires
     * trackOffer('impression', …) straight after painting, and that is correct
     * THERE because a modal is visible by construction. This rail is not: it
     * lives inside a drawer another app can hide, and one did. With the
     * candy-rack embed present, this surface rendered SIX offer nodes and every
     * one measured 0 x 0 — a paint-time impression would have reported six
     * offers as seen that no human could see.
     *
     * That direction of error is the dangerous one. The bug being fixed here
     * makes the cart unable to learn, and a frozen counter announces itself. An
     * inflated one does not: it is indistinguishable from real traffic at every
     * layer we measure, and the reader gates on
     * impressions + soft_impressions >= 30, so poisoning the numerator quietly
     * teaches the bandit from offers nobody saw.
     *
     * ⚠️ AND IT IS A FORWARD RISK, NOT A CLOSED ONE: 16 third-party app embeds
     * return to this theme at publish, and any one of them can hide the drawer
     * again. The zero-size guard below is the load-bearing line, not the tidy
     * one — do not remove it because "isIntersecting already implies visible".
     *
     * Idiom follows placements.js:528-542 (the one other IntersectionObserver in
     * this theme — a precedent, not a convention). It differs in two ways, both
     * deliberate: it observes MANY nodes so it unobserves per-card rather than
     * disconnecting on the first hit, and it carries the explicit size check.
     *
     * Bound worth knowing: a `visibility: hidden` ancestor still produces a
     * non-zero rect, so this guard catches the display:none / detached case we
     * actually observed and not that one. soft_impressions is untouched.
     */
    function observeRecImpressions() {
      if (!('IntersectionObserver' in window)) return;
      /* `myGen` and `io` are captured PER OBSERVER on purpose. The previous
         version read the shared `recIO` inside the callback, so a stale
         observer's queued entry would unobserve through — and be judged
         against — whatever the CURRENT render had installed. */
      var myGen = recGen;
      var io = new IntersectionObserver(function (entries) {
        if (myGen !== recGen) return;   /* this observer's render is gone */
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;              /* may intersect later — keep watching */
          var card = en.target;
          var id = card.getAttribute('data-cd-rec');
          var rec = id && recState[id];
          /* The record is gone: this card can never resolve. Stop watching it. */
          if (!rec || !rec.offer) { io.unobserve(card); return; }
          if (rec.seen) { io.unobserve(card); return; }
          /* THE ZERO-SIZE GUARD — explicit, beside the intersection check. */
          var box = en.boundingClientRect || card.getBoundingClientRect();
          if (!box || box.width <= 0 || box.height <= 0) return;   /* suppressed — keep watching */
          /* 🔑 A PIXEL FLOOR, NOT A FRACTION. This rail sits inside
             `.cd-body { overflow-y: auto }`. On a SHORT container the old
             `threshold: 0.5` was unreachable: sweeping a 0-644 scroll range, a
             367px card peaked at ratio 0.4808 — 176.49 visible px against the
             183.54 that 0.5 demands, seven pixels short at every position. On a
             TALLER container (measured `clientHeight` 521) the same card reaches
             ratio 1.0 and 0.5 would have fired.

             ⚠️ SO THE OLD BUG WAS VIEWPORT-DEPENDENT, NOT UNIVERSAL — AND THAT IS
             WORSE. An impression that fires on a tall window and silently never
             fires on a short laptop is the environment-shaped failure nobody
             reproduces on demand, and no static reading of this file could have
             found it. (An earlier version of this comment said "structurally…
             could never fire", which was true of the short container only.)

             The old 0.5 was copied from placements.js:541, which observes a
             page-level SECTION with no clipping ancestor; the constant carried an
             unstated precondition and did not survive the move.

             A fraction also asks the wrong question: at 0.5 a 367px card needs
             183px of exposure and a 200px card needs 100px — the SAME customer
             exposure, two different verdicts, purely because of card height. A
             pixel floor asks what we actually mean by "seen". `intersectionRect`
             is used because it is clip-aware; boundingClientRect is not, which is
             precisely how this defect hid from every static pass.

             The floor degrades for short cards (min with the card's own height),
             so a card shorter than the floor can still qualify by being wholly
             visible — a fraction cannot become unreachable that way, but a bare
             floor could. */
          var vis = (en.intersectionRect && en.intersectionRect.height) || 0;
          if (vis < Math.min(SEEN_MIN_PX, box.height)) return;   /* not enough of it — keep watching */
          /* 🔑 MARK SEEN ONLY IF AN EVENT WAS ACTUALLY QUEUED, AND NOT BEFORE.
             trackRec returns false when there is no attribution token, and in
             that case NOTHING is sent. Marking first would burn this offer's
             only impression on a send that never happened — the same mistake the
             zero-size guard exists to prevent, one failure path over: do not
             record "seen" for a view we could not report. Both exits leave the
             card observed so a later render with a token can still count it. */
          if (!trackRec('impression', rec.offer, rec.sel)) return;
          rec.seen = true;
          io.unobserve(card);
        });
         /* MANY thresholds, not one. A single `threshold: 0` fires only when the
            card CROSSES into intersecting — at perhaps one visible pixel — and
            never again as it scrolls further in, so the pixel floor above would
            be tested once, against the worst moment, and never re-tested. The
            ladder gives the observer a reason to call back as exposure grows,
            so the floor is evaluated near the point it is actually crossed. */
      }, { threshold: SEEN_STEPS });
      recIO = io;
      Object.keys(recState).forEach(function (id) {
        var card = el('[data-cd-rec="' + CSS.escape(id) + '"]', rootEl);
        if (card) io.observe(card);
      });
    }
    function hydrateRecSelectors() {
      Object.keys(recState).forEach(function (id) {
        var rec = recState[id];
        if (!rec.offer.handle) return;
        Products.get(rec.offer.handle).then(function (prod) {
          if (!prod || rootEl.hidden) return;
          var variants = (prod.variants || []).filter(function (v) { return v.available !== false; });
          if (variants.length < 2) return;
          rec.variants = variants;
          var sel = variants.filter(function (v) { return String(v.id) === String(rec.sel); })[0] || variants[0];
          rec.sel = sel.id;
          var card = el('[data-cd-rec="' + CSS.escape(id) + '"]', rootEl);
          if (!card) return;
          var mount = el('[data-cd-rec-sel]', card);
          if (mount) mount.innerHTML = '<div class="up-selwrap crec-sel"><span class="up-sellbl">' + esc(axisLabel(variants)) + '</span><label class="gsel"><select data-cd-rec-selopt>' +
            variants.map(function (v) { return '<option value="' + esc(v.id) + '"' + (String(v.id) === String(rec.sel) ? ' selected' : '') + '>' + esc(v.public_title || v.title) + '</option>'; }).join('') +
            '</select>' + ICON.chevDown + '</label></div>';
          var pr = el('[data-cd-rec-price]', card);
          if (pr) pr.textContent = moneyMinor(sel.price);
          syncDealLine(card, rec.offer, rec.sel);
        }).catch(function () {});
      });
    }

    function addRec(card) {
      var rec = recState[card.getAttribute('data-cd-rec')];
      if (!rec) return;
      var variant = rec.variants ? rec.variants.filter(function (v) { return String(v.id) === String(rec.sel); })[0] : null;
      var variantId = variant ? variant.id : rec.offer.variantId;
      if (!variantId) return;
      var addBtn = el('[data-cd-rec-add]', card);
      if (addBtn) addBtn.setAttribute('aria-busy', 'true');
      card.classList.add('added');
      var props = offerAcceptProps(rec.offer, {
        requestId: offerData && offerData.requestId,
        attributionToken: offerData && offerData.attributionToken,
        surface: 'cart',
        variantId: variantId,
        position: rec.pos
      });
      var addItem = { id: Number(variantId), quantity: 1 };
      if (Object.keys(props).length) addItem.properties = props;
      /* silent: adds initiated INSIDE the drawer must NOT re-emit
         exon:cart:added (that would pop the popup on top of the drawer) */
      window.EXON.cart.add(addItem, { silent: true }).then(function () {
        trackRec('accept', rec.offer, variantId);
        trackRec('add_to_cart', rec.offer, variantId);
      }).catch(function () { card.classList.remove('added'); if (addBtn) addBtn.removeAttribute('aria-busy'); });
    }

    /* -------- warranty order-bump -------- */
    function warrantyVariantId() {
      return CFG.warranty && CFG.warranty.variantId ? String(CFG.warranty.variantId).replace(/\D/g, '') : '';
    }
    /* handle for the price lookup: the config blob may omit it, so fall back
       to the handle the drawer snippet stamps on [data-cd-warr]. */
    function warrantyHandle() {
      return (CFG.warranty && CFG.warranty.handle) ||
        (warrEl && warrEl.getAttribute('data-cd-warr-handle')) || '';
    }
    function warrantyLine(cart) {
      var id = warrantyVariantId();
      if (!id) return null;
      return ((cart && cart.items) || []).filter(function (it) { return String(it.variant_id || it.id) === id; })[0] || null;
    }
    function syncWarranty(cart) {
      if (!warrEl) return;
      var id = warrantyVariantId();
      if (!CFG.warranty || !CFG.warranty.enabled || !id) { warrEl.hidden = true; return; }
      warrEl.hidden = false;
      var line = warrantyLine(cart);
      warrEl.classList.toggle('on', !!line);
      var toggle = el('[data-cd-warr-toggle]', warrEl);
      if (toggle) toggle.checked = !!line;
      /* price: prefer the live cart line; else pull the product once */
      var priceEl = el('[data-cd-warr-price]', warrEl);
      if (priceEl && !priceEl.textContent) {
        if (line) priceEl.textContent = moneyMinor(line.final_price || line.price);
        else Products.get(warrantyHandle()).then(function (prod) {
          var v = prod && (prod.variants || []).filter(function (x) { return String(x.id) === id; })[0];
          if (v && priceEl) {
            priceEl.innerHTML = esc(moneyMinor(v.price)) + (v.compare_at_price && v.compare_at_price > v.price ? ' <s>' + esc(moneyMinor(v.compare_at_price)) + '</s>' : '');
          }
        }).catch(function () {});
      }
    }
    function toggleWarranty(on) {
      if (warrantyBusy) return;
      var id = warrantyVariantId();
      if (!id) return;
      warrantyBusy = true;
      if (warrEl) warrEl.setAttribute('aria-busy', 'true');
      var cart = window.EXON.cart.state;
      var p;
      /* silent: the warranty add is a drawer-internal write — no re-pop */
      if (on) p = window.EXON.cart.add({ id: Number(id), quantity: 1 }, { silent: true });
      else {
        var line = warrantyLine(cart);
        p = line ? window.EXON.cart.change(line.key, 0) : Promise.resolve();
      }
      p.then(function () {}).catch(function () {}).then(function () {
        warrantyBusy = false;
        if (warrEl) warrEl.removeAttribute('aria-busy');
        syncWarranty(window.EXON.cart.state);
      });
    }

    /* -------- telemetry -------- */
    /* Returns TRUE only when the event was actually ENQUEUED, and FALSE on every
       path that drops it silently — no attribution token, or telemetry disabled.
       Callers that record "this offer has now been counted" must gate on it.

       ⚠️ THE TELEMETRY CHECK IS NOT REDUNDANT: Events.track() returns early when
       TELEMETRY is off (:247), so without this guard the function would return
       TRUE while nothing whatever was queued — and a caller would mark an offer
       counted on a store with telemetry disabled.

       ⚠️ ENQUEUED IS NOT DELIVERED, and the gap is not theoretical here: the
       batch is spliced out of the queue BEFORE the send, the fetch failure is
       swallowed by design, and a flush racing a navigation can be cancelled.
       So `true` means "we handed it to the telemetry queue", never "the server
       received it". Do not build a delivery guarantee on this return. */
    function trackRec(event, o, variantId) {
      if (!TELEMETRY) return false;
      if (!offerData || !offerData.attributionToken) return false;
      Events.track({
        event: event, surface: 'cart',
        attribution_token: offerData.attributionToken,
        request_id: offerData.requestId || undefined,
        target_id: o.targetId || undefined,
        /* The learning keys, same contract as trackOffer's — see the full note
           there (EXO-2254). `axis` is snake_case ONLY and has no camelCase
           fallback, unlike genre_bucket / anchor_variant_gid; empty fails like
           absent, hence `|| undefined`.

           📌 This surface used to fire ONLY 'accept' and 'add_to_cart', which
           meant cart rows accumulated accepts against an impression count they
           could not raise — the reader gates on
           impressions + soft_impressions >= min_observations (30), so the cart
           could never cross the threshold at any point in this file's history.
           It now also fires 'impression', from observeRecImpressions() — ON
           VISIBILITY, not on paint, because this rail can be rendered and then
           hidden by another app. Read that function's note before changing
           either call site: the guard order there is the property, not a
           detail.

           All FOUR keys, for the reasons in trackOffer's note: the stats
           table is keyed on six columns and two of them (`genre_bucket`,
           and `anchor_vendor_bucket` which the server resolves from
           `anchor_variant_gid`) default to 'other'/'all' if unsent, filing
           the row where nothing reads. `genre_bucket` has NO server-side
           repair branch — unsent means 'other' forever. And `|| undefined`
           is mandatory, not tidy: coalesce(snake, camel) takes the first
           NON-NULL, so an empty string SHADOWS a valid camelCase value. */
        metadata: {
          product_gid: o.productGid || '',
          target_variant_gid: variantId ? 'gid://shopify/ProductVariant/' + variantId : (o.variantGid || ''),
          offer_type: o.offerType || '',
          axis: o.axis || undefined,
          selection_arm_id: o.selectionArmId || undefined,
          genre_bucket: o.genreBucket || undefined,
          anchor_variant_gid: o.anchorVariantGid || undefined
        }
      });
      return true;
    }

    /* -------- events (delegated) -------- */
    rootEl.addEventListener('click', function (e) {
      var t = e.target;
      if (t === rootEl || t.closest('[data-cd-close]')) { e.preventDefault(); return close(); }
      if (t.closest('[data-cd-checkout]')) { e.preventDefault(); Events.flush(false); close(); GiftClaim.beforeCheckout().then(function () { window.location = '/checkout'; }); return; }

      var dn = t.closest('[data-cd-qty-dn]'); if (dn) { e.preventDefault(); return qtyStep(dn.getAttribute('data-cd-qty-dn'), -1); }
      var up = t.closest('[data-cd-qty-up]'); if (up) { e.preventDefault(); return qtyStep(up.getAttribute('data-cd-qty-up'), 1); }
      var rm = t.closest('[data-cd-remove]'); if (rm) { e.preventDefault(); return setQty(rm.getAttribute('data-cd-remove'), 0); }
      var undo = t.closest('[data-cd-undo]'); if (undo) { e.preventDefault(); return revertLine(undo.getAttribute('data-cd-undo')); }
      var upg = t.closest('[data-cd-upgrade]'); if (upg) { e.preventDefault(); return upgradeLine(upg); }
      var recAdd = t.closest('[data-cd-rec-add]'); if (recAdd) { e.preventDefault(); var c = recAdd.closest('[data-cd-rec]'); if (c && !c.classList.contains('added')) addRec(c); return; }
    });
    rootEl.addEventListener('change', function (e) {
      var warrToggle = e.target.closest('[data-cd-warr-toggle]');
      if (warrToggle) return toggleWarranty(warrToggle.checked);
      var recSel = e.target.closest('[data-cd-rec-selopt]');
      if (recSel) {
        var card = recSel.closest('[data-cd-rec]');
        var rec = card && recState[card.getAttribute('data-cd-rec')];
        if (rec && rec.variants) {
          rec.sel = recSel.value;
          var v = rec.variants.filter(function (x) { return String(x.id) === String(rec.sel); })[0];
          var pr = el('[data-cd-rec-price]', card);
          if (v && pr) pr.textContent = moneyMinor(v.price);
          syncDealLine(card, rec.offer, rec.sel);
        }
      }
    });

    function qtyStep(key, dir) {
      var it = ((window.EXON.cart.state && window.EXON.cart.state.items) || []).filter(function (x) { return String(x.key) === String(key); })[0];
      if (!it) return;
      setQty(key, Math.max(dir < 0 ? 1 : 0, it.quantity + dir));
    }
    function setQty(key, qty) { window.EXON.cart.change(key, qty).catch(function () {}); }
    /* 1-based position of a line's upgrade panel among the painted panels,
       top-to-bottom (cart order = DOM order) — the accept's render position */
    function upgradePanelPosition(key, off) {
      var items = (window.EXON.cart.state && window.EXON.cart.state.items) || [];
      var pos = 0;
      for (var i = 0; i < items.length; i++) {
        var list = lineUpgrades[items[i].key];
        if (!list) continue;
        for (var j = 0; j < list.length; j++) {
          pos++;
          if (String(items[i].key) === String(key) && list[j] === off) return pos;
        }
      }
      return 0;
    }
    function upgradeLine(btn) {
      var key = btn.getAttribute('data-cd-upgrade');
      var toVariant = btn.getAttribute('data-cd-upg-variant');
      var orig = btn.getAttribute('data-cd-upg-orig');
      if (!key || !toVariant) return;
      /* one swap per line: the guard is taken synchronously, and BOTH panels
         of this line (edition + Game Pass) go non-actionable before the first
         request. Failure releases them; success re-renders the drawer from the
         cart, where the swapped line carries _original_variant and paints none. */
      if (upgradePending[key]) return;
      upgradePending[key] = 1;
      var lineBtns = els('[data-cd-upgrade="' + cssEsc(key) + '"]', rootEl);
      lineBtns.forEach(function (b) { if (b !== btn) { b.setAttribute('disabled', ''); b.setAttribute('aria-disabled', 'true'); } });
      btn.setAttribute('aria-busy', 'true');
      var it = ((window.EXON.cart.state && window.EXON.cart.state.items) || []).filter(function (x) { return String(x.key) === String(key); })[0];
      var qty = it ? it.quantity : 1;
      /* the panel's own offer — a line can carry two (edition, Game Pass) */
      var off = (lineUpgrades[key] || []).filter(function (o) { return String(o.variantId) === String(toVariant); })[0];
      if (off) {
        /* pre-stamp the outgoing line's snapshot so cart.upgrade carries the
           attribution onto the target add (then stamps _original_variant last) */
        stampCarriedProps(key, offerAcceptProps(off, {
          requestId: offerData && offerData.requestId,
          attributionToken: offerData && offerData.attributionToken,
          surface: 'cart',
          variantId: toVariant,
          position: upgradePanelPosition(key, off)
        }));
      }
      window.EXON.cart.upgrade(key, Number(toVariant), qty, orig).then(function () {
        delete upgradePending[key];
        if (off) { trackRec('accept', off, toVariant); trackRec('add_to_cart', off, toVariant); }
      }).catch(function () {
        delete upgradePending[key];
        btn.removeAttribute('aria-busy');
        lineBtns.forEach(function (b) { if (b !== btn) { b.removeAttribute('disabled'); b.removeAttribute('aria-disabled'); } });
      });
    }
    function revertLine(key) {
      var it = ((window.EXON.cart.state && window.EXON.cart.state.items) || []).filter(function (x) { return String(x.key) === String(key); })[0];
      if (!it || !it.properties || !it.properties._original_variant) return;
      window.EXON.cart.revert(key, it.quantity, it.properties._original_variant).catch(function () {});
    }

    return { open: open, close: close, isOpen: function () { return rootEl && !rootEl.hidden; } };
  })();

  /* ============================================================
     GOAL ROW (EXO-4667) — the goal rail and the gift ticket fold
     into ONE row (snippets/fold-row.liquid, data-xf-key="goal") on
     the drawer and the cart page; the popup's gift ticket is its own
     row (data-xf-key="gift"). This paints the row's five slots
     through window.ExonFold (assets/fold.js) and never touches what
     the row holds.

     It READS, never writes: activeGoal / giftCfg for the numbers,
     the RENDERED gift mount for the ticket (.fcg[data-fcg-state],
     its .fcg-msg / .fcg-note), and the composed Drop rail's own
     published attributes. Order, first match wins (PLAN.md D5):
     gift ready/busy > gift done > gift note > composed Drop rail >
     goal not reached > goal reached > no goal (row hidden).

     FOLDED BY DEFAULT, OPENS ON NEWS (owner, 2026-10-05T16:39Z). A
     row opens by itself only when something just CHANGED in this tab:
     - the goal was just REACHED: the cart's tier went below → reached
       while GoalRow watched (reachedNews). A page, drawer or popup that
       first renders with the goal already reached is not news;
     - a NEW notice appeared (its appearance token, below).
     The token names that change; fold.js keeps the seen-flags per
     surface, defers the open until the row is actually on screen (so
     a drawer opened after the change still opens once), and folds an
     auto-opened row again on the customer's next scroll or tap. That
     is why the drawer and popup OPENING repaints too
     (exon:overlay:opened).

     Defined BEFORE GiftClaim, whose paint() calls it last: it must
     exist before GiftClaim.boot paints for the first time, and it
     never throws, so the claim never sees it fail.
     ============================================================ */
  /* to_get: the store goal rail's own sentence (renderGoal, the goal text
     below a store goal). A gift goal says the ticket's GT.below instead,
     which is the gift rail's sentence too. */
  var XT = {
    he: { waiting: 'מחכה לכם', in_cart: 'בעגלה', to_go: 'עוד {a}', to_get: 'עוד {a} ותקבלו {r}', gift_below: 'עוד {a} למתנה · {r}', goal: 'היעד שלכם' },
    en: { waiting: 'Waiting for you', in_cart: 'In cart', to_go: '{a} to go', to_get: '{a} to go for {r}', gift_below: '{a} more to your gift · {r}', goal: 'Your goal' }
  }[LANG];

  var GoalRow = (function () {
    function ltr(s) { return '<span class="ltr">' + esc(s) + '</span>'; }
    function word(s) { return { kind: 'word', text: s }; }
    /* a short, stable name for a notice: it becomes part of a storage key */
    function sig(s) {
      var h = 5381;
      for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
      return 'n' + (h >>> 0).toString(36);
    }

    /* THE GOAL TIER this tab last saw ('below' | 'reached', against which
       target) and the token of the last below → reached change, kept for
       the TAB (sessionStorage; memory when storage is blocked) so the cart
       page's reload after an add still sees the crossing. The first
       observation, or a new target (a gift starting or ending), records
       the tier and is never news. */
    var TIER_KEY = 'exon_xf_tier';
    var tier = null;
    function loadTier() {
      if (tier) return tier;
      try {
        var t = JSON.parse(sessionStorage.getItem(TIER_KEY) || 'null');
        if (t && (t.tier === 'below' || t.tier === 'reached') && isFinite(t.target) && isFinite(t.n)) {
          tier = { tier: t.tier, target: Number(t.target), n: Number(t.n), tok: typeof t.tok === 'string' ? t.tok : null };
        }
      } catch (e) {}
      return tier || (tier = { tier: null, target: 0, n: 0, tok: null });
    }
    function saveTier() {
      try { sessionStorage.setItem(TIER_KEY, JSON.stringify(tier)); } catch (e) {}
    }
    /* the token of the goal just reached, or null when there is no such news */
    function reachedNews(cart) {
      var ag = activeGoal(cart);
      if (!(ag.target > 0)) return null;
      var t = loadTier();
      var now = ag.basis >= ag.target ? 'reached' : 'below';
      if (t.tier === null || t.target !== ag.target) {
        t.tier = now; t.target = ag.target; t.tok = null; saveTier();
        return null;
      }
      if (t.tier !== now) {
        t.tier = now;
        if (now === 'reached') { t.n++; t.tok = 'reached:' + ag.target + ':' + t.n; } else t.tok = null;
        saveTier();
      }
      return now === 'reached' ? t.tok : null;
    }

    /* THE ACTIVE NOTICE per surface: its signature and which appearance of
       it this is, kept for the TAB (sessionStorage; memory when storage is
       blocked), because the seen-flags it keys are kept for the tab too.
       - A notice that STAYS (repaints, a reload, another page) keeps its
         token, so it opens once: the unresolved-cleanup note is state.
       - A notice that GOES AWAY (the surface's ticket shows with no notice)
         clears it, so the same notice coming back is a new appearance.
       - An EVENT that arrives with a new document (the cart page's reload
         carries ?fcg=, GIFT_NOTICE_PARAM, read here before GiftClaim.boot
         strips it) is a new appearance on each surface the first time that
         document shows a notice.
       A hidden mount says nothing either way (it is also what the page
       renders before the claim's first paint), so it keeps the token. */
    var NOTICE_KEY = 'exon_xf_notice';
    var active = {};
    try {
      var stored = JSON.parse(sessionStorage.getItem(NOTICE_KEY) || '{}');
      Object.keys(stored || {}).forEach(function (k) {
        var a = stored[k];
        if (a && (typeof a.sig === 'string' || a.sig === null) && isFinite(a.n)) active[k] = { sig: a.sig, n: Number(a.n) };
      });
    } catch (e) {}
    var fromUrl = false;
    try { fromUrl = !!new URL(location.href).searchParams.get('fcg'); } catch (e) {}
    var eventTaken = {};
    function saveActive() {
      try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify(active)); } catch (e) {}
    }
    function noticeGone(surface) {
      var a = active[surface];
      if (a && a.sig !== null) { a.sig = null; saveActive(); }
    }
    function noticeToken(surface, s) {
      var a = active[surface] || (active[surface] = { sig: null, n: 0 });
      var fresh = fromUrl && !eventTaken[surface];
      eventTaken[surface] = true;
      if (a.sig !== s || fresh) { a.sig = s; a.n++; saveActive(); }
      return s + '.' + a.n;
    }
    /* what the rendered gift ticket shows, or null when it shows nothing */
    function ticket(mount, surface) {
      var t = mount && !mount.hidden ? el('.fcg', mount) : null;
      if (!t) return null;
      var st = t.getAttribute('data-fcg-state') || '';
      var n = el('.fcg-msg', t) || el('.fcg-note', t);
      var text = n ? String(n.textContent || '').trim() : '';
      if (!text) { noticeGone(surface); return { state: st, notice: null }; }
      return { state: st, notice: noticeToken(surface, sig(st + '|' + text)) };
    }
    /* A title with the reward in it: the reward is its own <bdi>, so a
       Latin name inside a Hebrew sentence is isolated, and the CSS
       (upsell.css .xf-t > .xf-rw) makes it the one part that shrinks, so an
       ellipsis clips the END of the name, never its start. The sentence
       around it is escaped; {a} is trusted markup (the amount). */
    /* A reward that mixes a Latin name with Hebrew ("Mystery Gift Silver
       במתנה") would resolve its <bdi> to LTR from its first letter, putting
       the Hebrew word next to the separator and clipping it from the wrong
       side; such a reward reads in the page's own direction instead. */
    var RTL_CHAR_RE = /[\u0590-\u05FF]/;
    function rewardTitle(tpl, amount, reward) {
      var fill = function (p) { return p.replace('{a}', function () { return amount; }); };
      var parts = esc(tpl).split('{r}');
      if (parts.length < 2) return fill(parts[0]);
      var tail = parts.slice(1).join('');
      var dir = ' dir="' + (HE && RTL_CHAR_RE.test(reward) ? 'rtl' : 'ltr') + '"';
      return (parts[0] ? '<span class="xf-tp">' + fill(parts[0]) + '</span>' : '') +
        '<bdi class="xf-rw"' + dir + '>' + esc(reward) + '</bdi>' +
        (tail ? '<span class="xf-tp">' + fill(tail) + '</span>' : '');
    }
    /* BELOW THE TARGET the row says what is MISSING. A gift: "עוד ₪102
       למתנה · <reward>", the reward last in reading order (Fable §5.1); a
       store goal keeps the rail's own sentence. The state slot is empty: the
       progress line under the row already shows how far along the order is.
       A title that named the reward alone read as a gift already granted. */
    function below(m, ag) {
      var rem = Math.max(0, ag.target - ag.basis);
      var amount = ltr(moneyMinor(rem));
      var tpl = ag.reward ? (ag.gift ? XT.gift_below : XT.to_get) : XT.to_go;
      /* the gift sentence already says "למתנה": a reward named "<name>
         במתנה" drops that last word here (Fable §5.1: "· Mystery Gift Silver") */
      var reward = ag.gift ? String(ag.reward).replace(/\s+במתנה\s*$/, '') || ag.reward : ag.reward;
      m.titleHtml = rewardTitle(tpl, amount, reward);
      m.state = null;
      m.bar = Math.min(100, Math.round((ag.basis / ag.target) * 100));
      return m;
    }

    /* the ticket's own states; null when the ticket is not one of them.
       news = reachedNews(): the gift was just unlocked in this tab. */
    function fromTicket(m, tk, news) {
      if (tk.state === 'ready' || tk.state === 'busy') {
        m.state = word(XT.waiting); m.status = 'action'; m.auto = tk.notice || news;
        return m;
      }
      if (tk.state === 'done') {
        m.state = word(XT.in_cart); m.status = 'done'; m.icon = 'check'; m.auto = tk.notice || news;
        return m;
      }
      if (tk.state === 'note') { m.auto = tk.notice; return m; }   // a notice, or a claim being undone
      return null;
    }

    /* drawer + cart page: the goal rail, the composed Drop rail and the gift, one row */
    function goalModel(row, cart, surface, news) {
      var panel = el('[data-xf-in]', row);
      var ag = activeGoal(cart);
      var m = { hidden: false, title: ag.reward || XT.goal, icon: 'gift', tone: 'amber', state: null, bar: null, status: 'idle', auto: null };
      var tk = ticket(panel && el('.fcg-mount', panel), surface);
      if (tk && fromTicket(m, tk, news)) return m;
      var dgr = panel && el('.dgr-mount', panel);
      if (dgr && !dgr.hidden) {
        var read = el('[data-dgr-read]', dgr), track = el('[data-dgr-track]', dgr);
        var txt = read ? String(read.textContent || '').trim() : '';
        var now = track ? Number(track.getAttribute('aria-valuenow')) : NaN;
        m.title = dgr.getAttribute('data-dgr-label-drop') || m.title;
        m.state = txt ? { kind: 'num', html: ltr(txt) } : null;
        m.bar = isFinite(now) ? now : null;
        m.status = dgr.getAttribute('data-dgr-state') === 'done' ? 'done' : 'idle';
        return m;
      }
      if (!(ag.target > 0)) { m.hidden = true; return m; }
      m.tone = 'teal';
      if (ag.basis < ag.target) return below(m, ag);
      m.state = { kind: 'num', html: ltr(moneyMinor(ag.basis) + ' / ' + moneyMinor(ag.target)) };
      m.bar = 100; m.status = 'done'; m.icon = 'check'; m.auto = news;
      return m;
    }

    /* popup: the gift ticket alone (the popup has no goal rail) */
    function giftModel(row, cart, surface, news) {
      var panel = el('[data-xf-in]', row);
      var tk = ticket(panel && el('.fcg-mount', panel), surface);
      if (!tk) return { hidden: true, auto: null };
      var ag = activeGoal(cart);
      var m = { hidden: false, title: ag.reward || XT.goal, icon: 'gift', tone: 'amber', state: null, bar: null, status: 'idle', auto: null };
      if (fromTicket(m, tk, news)) return m;
      if (tk.state === 'below' && ag.target > 0) below(m, ag);
      m.auto = tk.notice;
      return m;
    }

    var ROWS = [['drawer', 'goal', goalModel], ['cart', 'goal', goalModel], ['popup', 'gift', giftModel]];
    function paint() {
      var ExonFold = window.ExonFold;
      if (!ExonFold) return;   // no controller on this page: nothing to paint
      var cart = window.EXON && window.EXON.cart && window.EXON.cart.state;
      if (!cart) return;   // the Liquid slots stand until there is a cart to read
      var news = null;
      try { news = reachedNews(cart); } catch (e) {}
      ROWS.forEach(function (r) {
        try {
          var row = ExonFold.row(r[0], r[1]);
          if (row) ExonFold.set(row, r[2](row, cart, r[0], news));
        } catch (e) {}
      });
    }
    function safePaint() { try { paint(); } catch (e) {} }

    /* several triggers in one turn → one paint */
    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      Promise.resolve().then(function () { queued = false; safePaint(); });
    }

    /* THE COMPOSED DROP RAIL paints itself (drops-goal.js) after the cart
       event, a turn later than this row would. One observer per rail that
       sits in a row, on the island's own nodes only — never on the row it
       writes, so a paint can never feed itself. */
    function watchRails() {
      if (typeof MutationObserver !== 'function') return;
      els('.dgr-mount').forEach(function (m) {
        if (m.__xfWatched || !m.closest || !m.closest('[data-xf-in]')) return;
        m.__xfWatched = true;
        var mo = new MutationObserver(schedule);
        mo.observe(m, { attributes: true, attributeFilter: ['hidden', 'data-dgr-state'] });
        var read = el('[data-dgr-read]', m);
        if (read) mo.observe(read, { characterData: true, childList: true, subtree: true });
        var track = el('[data-dgr-track]', m);
        if (track) mo.observe(track, { attributes: true, attributeFilter: ['aria-valuenow'] });
      });
    }

    (function wire() {
      if (!window.EXON || !window.EXON.bus) { return setTimeout(wire, 40); }
      /* its own cart listener: the cart page has no claim config to lean on */
      window.EXON.bus.on('exon:cart', schedule);
      /* first view: the surface is on screen now, so a waiting auto-open can land */
      window.EXON.bus.on('exon:overlay:opened', function (e) {
        var id = e && e.detail && e.detail.id;
        if (id === 'exon-popup' || id === 'exon-drawer') safePaint();
      });
      /* after this file has run to the end (the giftGoal export is installed
         and the Drop rail has repainted with it) */
      Promise.resolve().then(function () {
        try { watchRails(); } catch (e) {}
        safePaint();
      });
    })();

    return { paint: safePaint };
  })();

  function cssEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }

  /* ============================================================
     GIFT CLAIM — spend the target, get the gift free (one unit).
     Its OWN element (.fcg), wearing the Drop ticket's visual shell
     but none of the Drop system: no entitlement, no reservation, no
     Drop code. Config is CFG.giftClaim, emitted by
     snippets/exon-popup.liquid from the shop metafield
     exon.gift_offer (the same object the server-side order guard
     reads). No metafield → no gift: the store goal, unchanged.

     BASIS — the one number every surface reads: the sum of the cart's
     lines' final_line_price (after line-level discounts) EXCLUDING the
     gift line this claim added (the line carrying GIFT_PROP = the
     offer key). The popup ticket, the drawer rail, the cart-page rail
     (Liquid mirror) and the claim button all gate on it.

     CLAIM — add exactly 1 × variantId (stamped GIFT_PROP) → add the
     hidden code BESIDE the codes already in the cart → VERIFY the gift
     line is quantity 1 at ₪0 and every code that was applying still
     applies. Any other outcome takes exactly that line back out,
     restores the cart's codes as they were, and says so: a paid gift is
     never left in the cart looking free, and a customer's own code is
     never dropped for the gift.

     GUARD — on every cart change: our gift line below the threshold,
     outside the window, priced above ₪0, or above quantity 1 is
     corrected (removed, or set back to 1) and a notice says why. The
     server-side order guard is a separate system; this is the storefront
     half only.
     ============================================================ */
  var GIFT_PROP = '_exon_gift';
  var GIFT_NOTICE_PARAM = 'fcg';   // carries a guard/claim notice across the cart page's reload

  /* the config is usable only when every key the claim relies on is there
     and the offer is priced in the cart's own currency; anything else is
     no offer at all (the store goal shows) — never a half-configured claim */
  function giftCfg() {
    var g = CFG.giftClaim;
    if (!g || typeof g !== 'object') return null;
    if (!g.variantId || !g.code || !g.key || !g.startAt || !('endAt' in g)) return null;
    if (!(num(g.targetMinor) > 0) || !(num(g.valueMinor) > 0)) return null;
    if (!g.rewardHe || !g.rewardEn) return null;
    if (!g.currency || String(g.currency).toUpperCase() !== String(CFG.currency || '').toUpperCase()) return null;
    return g;
  }
  /* [startAt, endAt): an unreadable start is closed; endAt null is open-ended,
     any other endAt that does not read as a time is closed */
  function giftLive(now) {
    var g = giftCfg();
    if (!g) return false;
    var t = now || Date.now();
    var s = Date.parse(g.startAt);
    if (!isFinite(s) || t < s) return false;
    if (g.endAt !== null) {
      var e = Date.parse(g.endAt);
      if (!isFinite(e) || t >= e) return false;
    }
    return true;
  }
  function isOurGiftLine(it) {
    var g = giftCfg();
    return !!(g && it && String(it.variant_id || it.id) === String(g.variantId) &&
      it.properties && it.properties[GIFT_PROP] === String(g.key));
  }
  function giftLines(cart) {
    return ((cart && cart.items) || []).filter(isOurGiftLine);
  }
  function giftBasis(cart) {
    var sum = 0;
    ((cart && cart.items) || []).forEach(function (it) {
      if (isOurGiftLine(it)) return;
      sum += num(it.final_line_price != null ? it.final_line_price : it.line_price);
    });
    return sum;
  }
  /* any line of the gift VARIANT (ours or a paid copy): the code discounts
     that variant, so a second claim beside it is never offered */
  function giftVariantInCart(cart) {
    var g = giftCfg();
    return !!g && ((cart && cart.items) || []).some(function (it) {
      return String(it.variant_id || it.id) === String(g.variantId);
    });
  }
  /* THE goal every rail paints: the gift while live, the store goal otherwise */
  function activeGoal(cart) {
    if (giftLive()) {
      var g = giftCfg();
      return { target: num(g.targetMinor), basis: giftBasis(cart), reward: (HE ? g.rewardHe : g.rewardEn) || '', gift: true };
    }
    return {
      target: num(CFG.goal && CFG.goal.targetMinor),
      basis: num(cart && cart.total_price),
      reward: ((CFG.goal && (HE ? CFG.goal.rewardHe : CFG.goal.rewardEn)) || ''),
      gift: false
    };
  }
  function isOurCode(c) {
    var g = giftCfg();
    return String(c || '').toUpperCase() === String(g && g.code || '').toUpperCase();
  }
  /* the codes the customer entered, exactly as the cart holds them (dead ones
     included: they are the customer's, and they are put back as they were) */
  function customerCodes(cart) {
    var list = cart && cart.discount_codes;
    if (!Array.isArray(list)) return null; // unknown → the claim does not run
    return list.filter(function (d) { return d && d.code && !isOurCode(d.code); });
  }

  var GT = {
    he: {
      below: 'עוד {a} ל-{r}',
      below_sub: 'בהזמנה של {t} ומעלה, לא כולל המתנה',
      ready: 'הגעתם ל-{t}! {r}',
      ready_sub: 'שווי {v} · בחינם בהזמנה הזו',
      add: 'הוספת המתנה בחינם',
      busy: 'מוסיפים את המתנה לעגלה...',
      done: 'בעגלה: {r}',
      done_sub: '₪0 במקום {v}',
      err: 'לא הצלחנו להוסיף את המתנה בחינם, ולכן היא לא נוספה. נסו שוב.',
      err_code: 'כבר יש קוד הנחה בעגלה, והמתנה לא יכולה להתווסף יחד איתו.',
      removed: 'המתנה הוסרה מהעגלה: ההזמנה ירדה מתחת ל-{t}.',
      removed_paid: 'המתנה הוסרה מהעגלה כי לא הצלחנו לאשר אותה בחינם.',
      stub: 'מתנה'
    },
    en: {
      below: '{a} more for {r}',
      below_sub: 'On orders of {t} or more, gift excluded',
      ready: "You're at {t}! {r}",
      ready_sub: 'Worth {v} · free with this order',
      add: 'Add the free gift',
      busy: 'Adding the gift to your cart...',
      done: 'In your cart: {r}',
      done_sub: '₪0 instead of {v}',
      err: "We couldn't add the gift for free, so it was not added. Try again.",
      err_code: "A discount code is already in your cart, and the gift can't be added with it.",
      removed: 'The gift was removed: your order dropped below {t}.',
      removed_paid: "The gift was removed because we couldn't confirm it free.",
      stub: 'GIFT'
    }
  }[LANG];
  /* a gift string with the offer's target filled in ({t}, plain text) */
  function gt(k) {
    var g = giftCfg();
    return String(GT[k] || '').replace('{t}', g ? moneyMinor(num(g.targetMinor)) : '');
  }
  var GIFT_ICON = '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';
  var GIFT_CHECK = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  var GiftClaim = (function () {
    var busy = false;       // a claim is in flight
    var inflight = null;    // the guard run in flight (a promise)
    var msg = null;         // {kind:'err'|'notice', text}

    /* A FAILED CLAIM'S CLEANUP until a fresh cart read proves it done: no
       gift line of ours left, and the customer's codes back exactly as they
       were. Held in memory and in session storage (the cart page reloads), so
       a cleanup that failed on the network is finished by the next guard run
       instead of being forgotten. */
    var PENDING_KEY = 'exon_gift_pending';
    var pending = null;     // {key, prior: [code strings]}
    function setPending(p) {
      pending = p;
      try {
        if (p) sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
        else sessionStorage.removeItem(PENDING_KEY);
      } catch (e) {}
    }
    function loadPending() {
      try {
        var p = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
        if (p && typeof p.key === 'string' && Array.isArray(p.prior)) pending = p;
      } catch (e) {}
    }

    /* one mount per surface, created beside that surface's own anchors */
    function mountFor(surface) {
      var host, anchor, where;
      if (surface === 'popup') {
        host = el('[data-exon-popup]'); if (!host) return null;
        anchor = el('.up-ctx', host); where = 'before';
      } else if (surface === 'drawer') {
        host = el('[data-exon-drawer]'); if (!host) return null;
        anchor = el('[data-cd-goal]', host) || el('[data-drop-ticket]', host); where = anchor && anchor.hasAttribute('data-cd-goal') ? 'after' : 'before';
      } else {
        host = document; anchor = el('.ct-goal') || el('[data-drop-ticket][data-dtk-surface="cart"]');
        where = anchor && anchor.classList.contains('ct-goal') ? 'after' : 'before';
      }
      var served = el('[data-fcg="' + surface + '"]', host); if (served) return served; // EXO-4667: the mount the fold row renders
      if (!anchor || !anchor.parentNode) return null;
      var m = el('[data-fcg="' + surface + '"]', anchor.parentNode);
      if (!m) {
        m = document.createElement('div');
        m.className = 'fcg-mount';
        m.setAttribute('data-fcg', surface);
        m.hidden = true;
        anchor.parentNode.insertBefore(m, where === 'after' ? anchor.nextSibling : anchor);
      }
      return m;
    }

    function stateOf(cart) {
      if (busy) return 'busy';
      if (pending) return 'unresolved'; // never 'done' while a failed claim is being undone
      if (giftLines(cart).length) return 'done';
      var basis = giftBasis(cart);
      var g = giftCfg();
      if (basis >= num(g.targetMinor) && !giftVariantInCart(cart)) return 'ready';
      if (basis >= num(g.targetMinor)) return 'none'; // a paid copy of the gift is already here
      return 'below';
    }

    function html(surface, st, cart) {
      var g = giftCfg();
      var reward = (HE ? g.rewardHe : g.rewardEn) || '';
      var val = moneyMinor(num(g.valueMinor));
      var head = '', sub = '', extra = '';
      if (st === 'below') {
        var basis = giftBasis(cart), target = num(g.targetMinor);
        var rem = Math.max(0, target - basis);
        var pct = Math.min(100, Math.round((basis / target) * 100));
        head = esc(GT.below).replace('{a}', '<span class="ltr">' + esc(moneyMinor(rem)) + '</span>').replace('{r}', esc(reward));
        sub = esc(gt('below_sub'));
        extra = '<span class="fcg-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '"><i style="width:' + pct + '%"></i></span>';
      } else if (st === 'ready' || st === 'busy') {
        head = esc(gt('ready')).replace('{r}', esc(reward));
        sub = esc(GT.ready_sub).replace('{v}', '<s class="ltr">' + esc(val) + '</s>');
        extra = '<button class="fcg-go" type="button" data-fcg-claim' + (st === 'busy' ? ' disabled aria-busy="true"' : '') + '>' +
          (st === 'busy' ? esc(GT.busy) : esc(GT.add)) + '</button>';
      } else if (st === 'done') {
        head = esc(GT.done).replace('{r}', esc(reward));
        sub = esc(GT.done_sub).replace('{v}', '<s class="ltr">' + esc(val) + '</s>');
      } else if (msg) {
        /* a note-only ticket: the message IS the headline */
        head = esc(msg.text);
        return '<div class="fcg" data-fcg-state="note" data-kind="' + msg.kind + '">' +
          '<span class="fcg-rim" aria-hidden="true"></span>' +
          '<span class="fcg-stub">' + GIFT_ICON + '<small>' + esc(GT.stub) + '</small></span>' +
          '<span class="fcg-perf" aria-hidden="true"></span>' +
          '<span class="fcg-b" role="status"><b class="fcg-note">' + head + '</b></span></div>';
      }
      if (msg) extra += '<span class="fcg-msg" data-kind="' + msg.kind + '" role="status">' + esc(msg.text) + '</span>';
      return '<div class="fcg" data-fcg-state="' + st + '">' +
        '<span class="fcg-rim" aria-hidden="true"></span><span class="fcg-shine" aria-hidden="true"></span>' +
        '<span class="fcg-stub">' + (st === 'done' ? GIFT_CHECK : GIFT_ICON) + '<small>' + esc(GT.stub) + '</small></span>' +
        '<span class="fcg-perf" aria-hidden="true"></span>' +
        '<span class="fcg-b"><b>' + head + '</b>' + (sub ? '<em>' + sub + '</em>' : '') + extra + '</span>' +
        '</div>';
    }

    function paintOne(surface, cart) {
      var m = mountFor(surface);
      if (!m) return;
      if (!giftLive() || !cart) { m.hidden = true; m.innerHTML = ''; return; }
      var st = stateOf(cart);
      if (st === 'unresolved') {
        /* the claim's own error notice stays up until its cleanup is proven */
        if (!msg) msg = noteFromKey('err');
        st = 'none';
        m.innerHTML = html(surface, st, cart);
        m.setAttribute('data-fcg-surface', surface);
        m.hidden = false;
        return;
      }
      /* the popup carries the goal itself (it has no rail); the drawer and the
         cart page already paint the retargeted rail, so their ticket appears
         only when there is something to DO or to know */
      if (st === 'none' || (surface !== 'popup' && st === 'below' && !msg)) { m.hidden = true; m.innerHTML = ''; return; }
      m.innerHTML = html(surface, st === 'below' && surface !== 'popup' ? 'none' : st, cart);
      m.setAttribute('data-fcg-surface', surface);
      m.hidden = false;
    }
    function paint() {
      var cart = window.EXON && window.EXON.cart && window.EXON.cart.state;
      ['popup', 'drawer', 'cart'].forEach(function (s) { paintOne(s, cart); });
      try { GoalRow.paint(); } catch (e) {}
    }

    function onCartPage() { return !!el('.ct-goal') || !!el('[data-drop-ticket][data-dtk-surface="cart"]'); }
    /* the cart page is server-rendered: after a cart mutation it re-renders
       by navigation, carrying the notice (a fixed key, never free text) in
       the query so the reloaded page can say what happened */
    var NOTICE_KEYS = { removed: 'removed', removed_paid: 'removed_paid', err: 'err', err_code: 'err_code' };
    function settleCartPage(notice) {
      if (!onCartPage()) return false;
      var u = new URL(location.href);
      u.searchParams.delete(GIFT_NOTICE_PARAM);
      if (notice && notice.key) u.searchParams.set(GIFT_NOTICE_PARAM, notice.key);
      location.replace(u.toString());
      return true;
    }
    function noteFromKey(k) {
      if (!NOTICE_KEYS[k]) return null;
      return { kind: (k === 'err' || k === 'err_code') ? 'err' : 'notice', key: k, text: gt(k) };
    }

    async function removeLine(key) {
      return window.EXON.cart.change(key, 0);
    }
    /* sets the cart's codes to exactly `codes` (an array of strings) */
    async function setCodes(codes) {
      var res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      });
      if (!res.ok) throw Object.assign(new Error('discount_update_failed'), { code: 'discount_update_failed' });
      var next = await res.json().catch(function () { return null; });
      if (!next || !Array.isArray(next.items)) throw Object.assign(new Error('discount_update_unknown'), { code: 'discount_update_unknown' });
      window.EXON.cart.state = next;
      window.EXON.bus.emit('exon:cart', next);
      return next;
    }
    /* takes our code out and leaves every customer code where it was */
    async function clearOurCode() {
      var cur = await window.EXON.cart.refresh();
      var list = cur && cur.discount_codes;
      if (!Array.isArray(list) || !list.some(function (d) { return d && isOurCode(d.code); })) return;
      await setCodes(customerCodes(cur).map(function (d) { return String(d.code); }));
    }

    /* NOT EXON.cart.applyDiscount: that helper refuses whenever any code is in
       the cart and replaces the whole code list. The claim ADDS our code beside
       the customer's codes and then checks that each code that was applying
       still applies: when Shopify cannot carry both (a code that combines with
       nothing, e.g. an EXON-DROP code), it throws existing_discount and the
       caller puts the customer's codes back exactly as they were. */
    async function applyOurCode(code, prior) {
      var applying = prior.filter(function (d) { return d.applicable !== false; })
        .map(function (d) { return String(d.code).toUpperCase(); });
      var next = await setCodes(prior.map(function (d) { return String(d.code); }).concat([String(code)]));
      var after = Array.isArray(next.discount_codes) ? next.discount_codes : [];
      var still = function (c) {
        return after.some(function (d) { return d && String(d.code || '').toUpperCase() === c && d.applicable !== false; });
      };
      if (!applying.every(still)) throw Object.assign(new Error('existing_discount'), { code: 'existing_discount' });
      return next;
    }

    /* the same list of codes, ours not among them (case as Shopify folds it) */
    function codesAre(cart, prior) {
      var list = cart && cart.discount_codes;
      if (!Array.isArray(list)) return false;
      var have = list.map(function (d) { return String(d && d.code || '').toUpperCase(); }).sort();
      var want = prior.map(function (c) { return String(c).toUpperCase(); }).sort();
      return have.join(',') === want.join(',');
    }
    /* one attempt at the pending cleanup; true only when a fresh read proves it */
    async function compensate() {
      var p = pending;
      if (!p) return true;
      try {
        var now = await window.EXON.cart.refresh();
        var ours = giftLines(now);
        for (var i = 0; i < ours.length; i++) await removeLine(ours[i].key);
        if (ours.length || !codesAre(now, p.prior)) await setCodes(p.prior);
        var fresh = await window.EXON.cart.refresh();
        if (giftLines(fresh).length || !codesAre(fresh, p.prior)) return false;
      } catch (e) { return false; }
      if (pending === p) setPending(null);
      return true;
    }

    async function claim() {
      if (busy || pending || !giftLive()) return;
      var g = giftCfg();
      var cart = await window.EXON.cart.refresh();
      if (stateOf(cart) !== 'ready') { paint(); return; }
      var prior = customerCodes(cart);
      if (prior === null) { msg = noteFromKey('err'); paint(); return; }
      busy = true; msg = null; paint();
      try {
        var props = {}; props[GIFT_PROP] = String(g.key);
        var afterAdd = await window.EXON.cart.add({ id: Number(g.variantId), quantity: 1, properties: props }, { silent: true });
        var mine = giftLines(afterAdd);
        if (mine.length !== 1) throw new Error('gift_line_not_found');
        var applied = await applyOurCode(g.code, prior);
        /* VERIFY on the response of the apply itself, then on a fresh read */
        var chk = giftLines(applied);
        if (chk.length !== 1 || num(chk[0].quantity) !== 1 || num(chk[0].final_line_price) !== 0) throw new Error('gift_not_free');
        var fresh = await window.EXON.cart.refresh();
        chk = giftLines(fresh);
        if (chk.length !== 1 || num(chk[0].quantity) !== 1 || num(chk[0].final_line_price) !== 0) throw new Error('gift_not_free');
        busy = false;
        trackGift('claim_ok');
        if (settleCartPage(null)) return;
        paint();
      } catch (err) {
        /* COMPENSATE: take the gift line back out and put the cart's codes
           back as they were — never a paid "gift" left in, never a customer's
           code lost. Every gift line goes: the claim starts only from 'ready'
           (no gift line in the cart), and a line's key changes when a code
           applies to it, so the key captured at add time cannot find it.
           The cleanup is recorded first and tried twice here; if it is still
           unproven, the guard (run once now, then on every cart event and
           before checkout) keeps at it. */
        setPending({ key: String(g.key), prior: prior.map(function (d) { return String(d.code); }) });
        var undone = (await compensate()) || (await compensate());
        busy = false;
        msg = noteFromKey((err && err.code === 'existing_discount') ? 'err_code' : 'err');
        trackGift('claim_fail', err && (err.code || err.message));
        if (!undone) trackGift('cleanup_pending');
        if (settleCartPage(msg)) return;
        paint();
        if (!undone) guard();
      }
    }

    /* the storefront guard: runs on every cart change; a call while a run is
       in flight joins that run */
    function guard() {
      if (busy) return Promise.resolve();
      if (inflight) return inflight;
      /* marked in flight BEFORE the run starts: a cart event emitted inside
         its first step must join it, not start a second one */
      var settle;
      inflight = new Promise(function (r) { settle = r; });
      var run = inflight;
      Promise.resolve().then(guardOnce).then(null, function () {}).then(function () {
        if (inflight === run) inflight = null;
        settle();
      });
      return run;
    }
    async function guardOnce() {
      var cart = window.EXON && window.EXON.cart && window.EXON.cart.state;
      if (!cart || !giftCfg()) return;
      if (pending) {
        /* a failed claim's cleanup first; the cart page re-renders only once
           it is proven, so an unreachable cart never loops the reload */
        if (await compensate()) {
          if (settleCartPage(msg)) return;
        }
        paint();
        return;
      }
      var mine = giftLines(cart);
      if (!mine.length) return;
      var g = giftCfg();
      var below = giftBasis(cart) < num(g.targetMinor);
      var closed = !giftLive();
      var paid = mine.some(function (l) { return num(l.final_line_price) > 0 && num(l.quantity) === 1; });
      var extra = mine.length > 1 || mine.some(function (l) { return num(l.quantity) > 1; });
      if (!below && !closed && !paid && !extra) return;
      var notice = null;
      try {
        if (below || closed || paid) {
          for (var i = 0; i < mine.length; i++) await removeLine(mine[i].key);
          await clearOurCode();
          notice = noteFromKey(below ? 'removed' : 'removed_paid');
          trackGift(below ? 'auto_remove_below' : (closed ? 'auto_remove_closed' : 'auto_remove_paid'));
        } else {
          /* one gift unit only: keep the first line at 1, drop the rest */
          await window.EXON.cart.change(mine[0].key, 1);
          for (var j = 1; j < mine.length; j++) await removeLine(mine[j].key);
        }
      } catch (e) { /* next cart event retries */ }
      if (notice) msg = notice;
      if (settleCartPage(notice)) return;
      paint();
    }

    function trackGift(kind, detail) {
      try { if (window.console && console.info) console.info('[gift]', kind, detail || ''); } catch (e) {}
    }

    function boot() {
      if (!giftCfg()) return;
      loadPending();
      try {
        var u = new URL(location.href);
        var k = u.searchParams.get(GIFT_NOTICE_PARAM);
        if (k) {
          msg = noteFromKey(k);
          u.searchParams.delete(GIFT_NOTICE_PARAM);
          history.replaceState(history.state, '', u.toString());
        }
      } catch (e) {}
      window.EXON.bus.on('exon:cart', function () {
        /* a message outlives exactly one repaint after the customer acts again */
        paint(); guard();
      });
      document.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-fcg-claim]');
        if (!b) return;
        e.preventDefault();
        claim();
      });
      if (window.EXON.cart.state) { paint(); guard(); }
    }

    /* checkout handlers wait for the guard (and any pending cleanup) to run;
       never rejects, so navigation always follows */
    function beforeCheckout() {
      return guard().catch(function () {});
    }

    return { boot: boot, paint: paint, beforeCheckout: beforeCheckout, clearMsg: function () { msg = null; } };
  })();


  /* ============================================================
     TRIGGERS — wire the surfaces to the bus + cart-open buttons.
     ============================================================ */
  function boot() {
    if (!window.EXON || !window.EXON.bus) { return setTimeout(boot, 40); }

    GiftClaim.boot();

    /* popup opens after an add resolves (exon-store emits exon:cart:added) */
    window.EXON.bus.on('exon:cart:added', function (e) {
      if (!POPUP_ENABLED) return;
      var detail = (e && e.detail) || {};
      var items = detail.items;
      var first = Array.isArray(items) ? items[0] : items;
      Popup.open({
        addedVariantId: first && (first.id || first.variantId),
        intent: detail.intent || 'cart'
      });
    });

    /* cart-open buttons (header + tab bar) open the drawer */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-exon-cart-open]');
      if (!btn) return;
      if (!DRAWER_ENABLED) return; // let the native /cart link work
      e.preventDefault();
      /* ensure fresh cart state, then open */
      if (window.EXON.cart.state) Drawer.open();
      else window.EXON.cart.refresh().then(function () { Drawer.open(); }).catch(function () { Drawer.open(); });
    }, true);
  }
  boot();

  /* expose for cross-island handoff (popup CTA → drawer) + debugging */
  window.ExonUpsell = { Popup: Popup, Drawer: Drawer, normalize: normalizeResponse };

  /* the composed Drop rail (drops-goal.js) asks for the live gift goal here
     instead of reading the config a second time: { target, reward, basis }
     in minor units while the gift is live, else null (the store goal). It
     loads before this file and painted without it, so it repaints now. */
  window.EXON.giftGoal = function (cart) {
    var ag = activeGoal(cart);
    return ag.gift ? { target: ag.target, reward: ag.reward, basis: ag.basis } : null;
  };
  try { if (window.ExonDropGoal) window.ExonDropGoal.repaint(); } catch (e) {}
})();
